import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { Viewer360Props } from '../types/Apartment';
import { useApartments } from '../context/ApartmentContext';
import { apartments as defaultApartments, getApartmentPDFName } from '../data/apartments';
import { Mouse, Pointer, Target, RotateCcw, ArrowLeft, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { formatPrice } from '../config/priceConfig';
import { useDeviceDetection, mediaQueries } from '../hooks/useDeviceDetection';

// Add SVG paths types - Updated for new structure
interface SVGPathData {
  path_data: string;
  fill: string;
  stroke: string;
  fill_opacity: string;
  fill_rule: string;
  all_attributes: Record<string, any>;
}

interface SVGApartment {
  id: string;
  data_apartment: string;
  paths: SVGPathData[];
  all_attributes: Record<string, any>;
}

interface SVGFrame {
  filename: string;
  frame_number: string;
  svg_attributes: {
    'data-frame': string;
    viewBox: string;
    width: string;
    height: string;
  };
  apartments: Record<string, SVGApartment>;
  full_svg_structure?: any;
}

interface SVGPathsData {
  metadata: {
    total_files: number;
    source_folder: string;
    processing_info: string;
  };
  frames: Record<string, SVGFrame>;
}

// Text paths types - assuming similar structure to SVG paths
interface SVGTextData {
  text_content?: string;
  x?: string;
  y?: string;
  font_family?: string;
  font_size?: string;
  fill?: string;
  text_anchor?: string;
  all_attributes?: Record<string, any>;
  // Path properties (from the actual JSON structure)
  d?: string;
  stroke?: string;
  fill_opacity?: string;
  fill_rule?: string;
}

// Simplified structure - the JSON file contains direct frame->apartment->textData mapping

interface SVGTextPathsData {
  [frameKey: string]: Record<string, SVGTextData[]>;
}

const MobileWrapper = styled.div`
  /* Mobile: Split screen layout with panning */
  @media ${mediaQueries.mobile} {
    display: flex;
    flex-direction: column;
    gap: 0;
    width: 100%;
    height: 100%;
    max-height: calc(100vh - 76px);
    min-height: calc(100vh - 76px);
    overflow: hidden;
    box-sizing: border-box;
    background: white;
    position: relative;
    touch-action: manipulation;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    overscroll-behavior: none;
  }

  /* Tablet: Fullscreen layout like desktop but touch-optimized */
  @media ${mediaQueries.tablet} {
    display: block;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
    box-sizing: border-box;
    background: #000;
    position: relative;
    touch-action: manipulation;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    overscroll-behavior: none;
  }
`;

const ViewerContainer = styled.div`
  position: relative;
  background: #000;
  overflow: hidden;
  flex-shrink: 0;

  /* Desktop: Fullscreen */
  @media ${mediaQueries.desktop} {
    width: 100vw;
    height: 100vh;
    padding-bottom: 0;
    border-radius: 0;
    box-shadow: none;
  }

  /* Tablet: Fullscreen like desktop but touch-optimized */
  @media ${mediaQueries.tablet} {
    width: 100vw;
    height: 100vh;
    padding-bottom: 0;
    border-radius: 0;
    box-shadow: none;
    overflow: hidden;
    touch-action: manipulation;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    overscroll-behavior: none;
  }

  /* Mobile: Split screen optimizations – more space for viewer, info panel below */
  @media ${mediaQueries.mobile} {
    width: 100%;
    height: auto;
    max-height: none;
    min-height: 0;
    padding-bottom: 0;
    border-radius: 0;
    box-shadow: none;
    flex: 1 1 auto;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
    overflow: hidden;
    touch-action: manipulation;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    overscroll-behavior: none;
  }
`;

const ImageContainer = styled.div<{ isDragging: boolean; isPanning: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  overflow: hidden;

  ${props => props.isDragging && css`
    cursor: grabbing;
  `}

  ${props => props.isPanning && css`
    cursor: move;
  `}

  /* Desktop: Standard rotation cursor */
  @media ${mediaQueries.desktop} {
    cursor: ${props => props.isDragging ? 'grabbing' : 'grab'};
  }

  /* Tablet: Touch-optimized rotation cursor */
  @media ${mediaQueries.tablet} {
    cursor: ${props => props.isDragging ? 'grabbing' : 'grab'};
    overflow: hidden;
  }

  /* Mobile: Swipe to rotate */
  @media ${mediaQueries.mobile} {
    cursor: ${props => props.isDragging ? 'grabbing' : 'grab'};
    overflow: hidden;
    touch-action: none;
  }
`;

// Maximum performance image stack - zero flicker at highest frame rates
const ImageStack = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* Maximum performance optimizations */
  contain: strict;
  transform: translate3d(0, 0, 0);
  will-change: auto;
  
  /* Prevent any layout shifts */
  overflow: hidden;
  
  /* Force GPU layer for instant switching */
  -webkit-transform: translate3d(0, 0, 0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
`;

const StackedPanoramaImage = styled.img<{ 
  panX: number; 
  isVisible: boolean;
  imageIndex: number;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
  
  /* GPU acceleration for maximum performance */
  transform: translate3d(0, 0, 0);
  will-change: transform;
  
  /* INSTANT switching using opacity for better performance than display */
  opacity: ${props => props.isVisible ? '1' : '0'};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  
  /* Force browser to keep in memory for instant switching */
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;

  /* Mobile: Full frame visible, rotation via swipe */
  @media (max-width: 850px) {
    object-fit: contain;
    width: 100%;
    height: 100%;
    min-width: unset;
    left: 0;
    transform: none;
  }
`;

const PanoramaImage = styled.img<{ panX: number }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;

  /* Mobile: Full frame visible, rotation via swipe */
  @media (max-width: 850px) {
    object-fit: contain;
    width: 100%;
    height: 100%;
    min-width: unset;
    left: 0;
    transform: none;
  }
`;

const MobileSwipeStrip = styled.div`
  display: none;

  @media (max-width: 850px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: 100%;
    flex-shrink: 0;
    height: 36px;
    min-height: 36px;
    padding: 0 12px;
    background: #fff;
    border-top: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    color: #94a3b8;
    touch-action: none;
    -webkit-tap-highlight-color: transparent;
  }
`;

const MobileSwipeArrow = styled.div`
  display: flex;
  align-items: center;
  color: #94a3b8;
  opacity: 0.85;
`;

const MobileControls = styled.div`
  display: none;
  
  @media (max-width: 850px) {
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    padding: 16px 16px calc(16px + env(safe-area-inset-bottom, 0px));
    backdrop-filter: none;
    box-shadow: none;
    border: none;
    height: auto;
    max-height: 42vh;
    min-height: 0;
    overflow-y: auto;
    flex-shrink: 0;
    border-top: none;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: manipulation;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    box-sizing: border-box;
    gap: 12px;
  }
`;

const MobileApartmentInfoPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const MobileApartmentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const MobileApartmentTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  font-family: 'Inter', sans-serif;
  letter-spacing: -0.02em;
  line-height: 1.25;
`;

const MobileApartmentClearButton = styled.button`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: #64748b;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background: #f1f5f9;
  }
`;

const MobileApartmentMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`;

const MobileApartmentMetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  text-align: center;
`;

const MobileApartmentMetaLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
`;

const MobileApartmentMetaValue = styled.span<{ $reserved?: boolean }>`
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.$reserved ? '#dc2626' : '#0f172a'};
  font-family: 'Inter', sans-serif;
`;

const MobileApartmentDetailsButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  font-size: 15px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.28);
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: scale(0.98);
  }
`;

const ControlSection = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ControlTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: 0.02em;
  padding: 8px 12px;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.08), rgba(22, 163, 74, 0.12));
  border-radius: 10px;
  border-left: 4px solid #22c55e;
  
  @media (max-width: 850px) {
    font-size: 16px;
    font-weight: 800;
    padding: 10px 14px;
    margin-bottom: 18px;
    border-radius: 12px;
    border-left-width: 5px;
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.15);
  }
`;

const ControlIcon = styled.span`
  font-size: 18px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 1px 2px rgba(34, 197, 94, 0.3));
  
  @media (max-width: 850px) {
    font-size: 20px;
  }
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RotationSlider = styled.input`
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: #e5e5e5;
  outline: none;
  -webkit-appearance: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #007AFF;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 122, 255, 0.3);
    transition: all 0.2s ease;
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 122, 255, 0.4);
  }

  &::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #007AFF;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 122, 255, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Modern Circular Rotation Control Components
const CircularRotationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(145deg, #f8fafc, #ffffff);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 850px) {
    /* Now we have full height again! */
    padding: 24px;
    border-radius: 20px;
    gap: 20px;
    max-width: 100%;
  }
`;

const CircularSlider = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  cursor: grab;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  
  &:active {
    cursor: grabbing;
  }
  
  @media (max-width: 850px) {
    /* Back to full size with more space available */
    width: 140px;
    height: 140px;
    /* Enhanced touch responsiveness on mobile */
    touch-action: none;
    -webkit-user-drag: none;
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    will-change: transform;
  }
`;

const CircularTrack = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border: 2px solid #e2e8f0;
  box-shadow: 
    inset 0 3px 12px rgba(15, 23, 42, 0.08),
    inset 0 -1px 4px rgba(255, 255, 255, 0.9),
    0 4px 16px rgba(15, 23, 42, 0.04),
    0 1px 2px rgba(15, 23, 42, 0.06);
`;

const CircularSliderProgress = styled.div<{ progress: number; isDragging: boolean }>`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: transparent;
  border: 2px solid transparent;
  transition: ${props => props.isDragging ? 'none' : 'background 0.1s ease-out'};
  filter: ${props => props.isDragging ? 'brightness(1.05) saturate(1.1)' : 'brightness(1)'};
  transform: ${props => props.isDragging ? 'scale(1.01)' : 'scale(1)'};
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: transparent;
    filter: blur(1px);
    transition: ${props => props.isDragging ? 'none' : 'background 0.1s ease-out'};
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 4px;
    border-radius: 50%;
    background: linear-gradient(145deg, #ffffff, #f8fafc);
    box-shadow: 
      inset 0 2px 8px rgba(15, 23, 42, 0.06),
      inset 0 -1px 2px rgba(255, 255, 255, 0.8);
  }
`;

const CircularHandle = styled.div<{ angle: number; isDragging: boolean }>`
  position: absolute;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #64748b, #475569);
  border: 3px solid #ffffff;
  box-shadow: 
    0 4px 16px rgba(71, 85, 105, 0.4),
    0 2px 8px rgba(15, 23, 42, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  top: 50%;
  left: 50%;
  transform: 
    translate(-50%, -50%) 
    rotate(${props => props.angle}deg) 
    translateY(-58px)
    ${props => props.isDragging ? 'scale(1.15)' : 'scale(1)'};
  transition: ${props => props.isDragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'};
  z-index: 3;
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
  
  &:hover {
    transform: 
      translate(-50%, -50%) 
      rotate(${props => props.angle}deg) 
      translateY(-58px)
      ${props => props.isDragging ? 'scale(1.15)' : 'scale(1.05)'};
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    background: transparent;
    filter: blur(4px);
    opacity: ${props => props.isDragging ? 1 : 0.6};
    transition: opacity 0.3s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 2px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.1));
  }
  
  @media (max-width: 850px) {
    width: 32px;
    height: 32px;
    border-width: 4px;
    transform: 
      translate(-50%, -50%) 
      rotate(${props => props.angle}deg) 
      translateY(-66px)
      ${props => props.isDragging ? 'scale(1.15)' : 'scale(1)'};
      
    &:hover {
      transform: 
        translate(-50%, -50%) 
        rotate(${props => props.angle}deg) 
        translateY(-66px)
        ${props => props.isDragging ? 'scale(1.15)' : 'scale(1.05)'};
    }
      
    &::before {
      inset: -4px;
    }
  }
`;

const CircularCenterDisplay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  z-index: 1;
`;

const CircularControlLabel = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;
  
  @media (max-width: 850px) {
    font-size: 20px;
  }
`;

const CircularControlSubtitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  @media (max-width: 850px) {
    font-size: 12px;
  }
`;

// Tablet Horizontal Scroll Bar Components
const TabletScrollBarContainer = styled.div`
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  max-width: 600px;
  z-index: 1000;
  display: none;
  
  @media (min-width: 851px) and (max-width: 1300px) {
    display: block;
  }
`;

const TabletScrollBarTrack = styled.div`
  width: 100%;
  height: 16px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const TabletScrollBarThumb = styled.div<{ position: number }>`
  position: absolute;
  top: -10px;
  left: ${props => props.position}%;
  transform: translateX(-50%);
  width: 50px;
  height: 40px;
  background: linear-gradient(135deg, #ffffff 0%, #e0e0e0 100%);
  border-radius: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  cursor: grab;
  transition: all 0.2s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  
  &:active {
    cursor: grabbing;
    transform: translateX(-50%) scale(1.1);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.4);
  }
  
  &:hover {
    transform: translateX(-50%) scale(1.05);
    box-shadow: 0 5px 22px rgba(0, 0, 0, 0.35);
  }
`;

const TabletScrollBarLabel = styled.div`
  text-align: center;
  margin-top: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
`;

const ProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 200px;
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.08), rgba(22, 163, 74, 0.12));
  border-radius: 12px;
  border: 1px solid rgba(34, 197, 94, 0.2);
  
  @media (max-width: 850px) {
    /* Full design with more space */
    max-width: 240px;
    padding: 14px 18px;
    border-radius: 14px;
  }
`;

const ProgressLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  
  @media (max-width: 850px) {
    font-size: 14px;
  }
`;

const ProgressValue = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: #16a34a;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &::before {
    content: '●';
    color: #22c55e;
    font-size: 8px;
    animation: pulse-indicator 1.5s ease-in-out infinite;
  }
  
  @keyframes pulse-indicator {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
  
  @media (max-width: 850px) {
    font-size: 15px;
  }
`;

const WheelInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding: 8px 12px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.9));
  border-radius: 8px;
  border: 1px solid rgba(226, 232, 240, 0.6);
  font-size: 13px;
  color: #475569;
  font-weight: 500;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  
  @media (max-width: 850px) {
    margin-top: 14px;
    padding: 10px 14px;
    font-size: 14px;
    border-radius: 10px;
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
  }
`;

const WheelValue = styled.div`
  font-weight: 700;
  color: #16a34a;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &::before {
    content: '●';
    color: #22c55e;
    font-size: 12px;
    animation: pulse-dot 2s ease-in-out infinite;
  }
  
  @keyframes pulse-dot {
    0%, 100% { opacity: 0.6; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.2); }
  }
  
  @media (max-width: 850px) {
    font-size: 15px;
    font-weight: 800;
  }
`;



const SliderInfo = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
  margin-top: 8px;
`;

const PanControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PanInfo = styled.div`
  flex: 1;
  font-size: 14px;
  color: #666;
  line-height: 1.4;
`;

const PanResetButton = styled.button`
  padding: 8px 16px;
  background: #374151;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  letter-spacing: 0.025em;

  &:hover {
    background: #1f2937;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }
`;

const PanIndicator = styled.div<{ panX: number; maxPan: number }>`
  width: 60px;
  height: 6px;
  background: #e5e5e5;
  border-radius: 3px;
  position: relative;
  margin: 8px 0;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(calc(-50% + ${props => (props.panX / props.maxPan) * 30}px));
    width: 20px;
    height: 6px;
    background: #007AFF;
    border-radius: 3px;
    transition: transform 0.1s ease-out;
  }
`;


const InstructionOverlay = styled.div<{ isVisible: boolean; isMobile: boolean }>`
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.85), rgba(31, 41, 55, 0.9));
  padding: 12px 20px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
  pointer-events: none;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 12px;
  opacity: ${props => (props.isVisible ? 1 : 0)};
  transition: all 0.4s ease-in-out;
  white-space: nowrap;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  letter-spacing: 0.3px;

  @media (max-width: 850px) {
    bottom: 12px;
    font-size: 13px;
    padding: 10px 16px;
    border-radius: 20px;
    gap: 10px;
    border-width: 1px;
  }

  /* Enhanced desktop styling with premium feel */
  @media (min-width: 851px) {
    bottom: 32px;
    font-size: 16px;
    padding: 18px 32px;
    border-radius: 30px;
    background: linear-gradient(135deg, 
      rgba(0, 0, 0, 0.9), 
      rgba(17, 24, 39, 0.95),
      rgba(31, 41, 55, 0.9)
    );
    border: 2px solid rgba(255, 255, 255, 0.25);
    box-shadow: 
      0 12px 48px rgba(0, 0, 0, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
    font-weight: 700;
    letter-spacing: 0.5px;
    animation: ${props => props.isVisible ? 'desktopGlow 3s ease-in-out infinite' : 'none'};
    
    @keyframes desktopGlow {
      0%, 100% { 
        transform: translateX(-50%) scale(1);
        border-color: rgba(255, 255, 255, 0.25);
        box-shadow: 
          0 12px 48px rgba(0, 0, 0, 0.5),
          inset 0 1px 0 rgba(255, 255, 255, 0.1);
      }
      50% { 
        transform: translateX(-50%) scale(1.03);
        border-color: rgba(59, 130, 246, 0.6);
        box-shadow: 
          0 16px 64px rgba(0, 0, 0, 0.6),
          0 0 32px rgba(59, 130, 246, 0.3),
          inset 0 1px 0 rgba(255, 255, 255, 0.2);
      }
    }
  }
`;

const CenterButtonOverlay = styled.button<{ isVisible: boolean }>`
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, rgba(31, 41, 55, 0.95), rgba(17, 24, 39, 0.98));
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: ${props => (props.isVisible ? 1 : 0)};
  transition: all 0.3s ease-in-out;
  white-space: nowrap;
  backdrop-filter: blur(12px);
  pointer-events: ${props => (props.isVisible ? 'auto' : 'none')};
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  letter-spacing: 0.5px;

  &:hover {
    background: linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(7, 12, 20, 1));
    transform: translateX(-50%) translateY(-2px);
    box-shadow: 
      0 12px 40px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:active {
    transform: translateX(-50%) translateY(-1px);
    box-shadow: 
      0 6px 24px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 850px) {
    bottom: 12px;
    font-size: 13px;
    padding: 10px 16px;
    border-radius: 10px;
  }

  /* Desktop styling */
  @media (min-width: 851px) {
    bottom: 24px;
    font-size: 15px;
    padding: 14px 22px;
    border-radius: 14px;
  }
`;

const MobileBackButton = styled.button`
  display: none;

  @media (max-width: 850px) {
    display: flex;
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 20;
    align-items: center;
    gap: 6px;
    padding: 10px 14px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    color: #1e293b;
    font-size: 14px;
    font-weight: 600;
    font-family: 'Inter', sans-serif;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    -webkit-tap-highlight-color: transparent;

    &:active {
      transform: scale(0.97);
    }
  }
`;


const CenterIcon = styled.span`
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(100, 116, 139, 0.2);
  color: #64748b;
  animation: centerPulse 2s ease-in-out infinite;
  
  @keyframes centerPulse {
    0%, 100% { 
      transform: scale(1);
      box-shadow: 0 0 10px rgba(100, 116, 139, 0.3);
    }
    50% { 
      transform: scale(1.1);
      box-shadow: 0 0 20px rgba(100, 116, 139, 0.5);
    }
  }

  @media (max-width: 850px) {
    font-size: 16px;
    width: 20px;
    height: 20px;
  }
`;

const HandIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  animation: professionalSwipe 2s ease-in-out infinite;

  @keyframes professionalSwipe {
    0%, 20% { 
      transform: translateX(6px) scale(1);
      box-shadow: 0 0 20px rgba(59, 130, 246, 0.4);
    }
    40%, 60% { 
      transform: translateX(-6px) scale(1.05);
      box-shadow: 0 0 30px rgba(59, 130, 246, 0.6);
    }
    80%, 100% { 
      transform: translateX(6px) scale(1);
      box-shadow: 0 0 20px rgba(59, 130, 246, 0.4);
    }
  }

  @media (max-width: 850px) {
    font-size: 18px;
    width: 28px;
    height: 28px;
    background: rgba(255, 255, 255, 0.12);
    
    @keyframes professionalSwipe {
      0%, 100% { 
        transform: translateX(4px) scale(1);
        box-shadow: 0 0 15px rgba(34, 197, 94, 0.4);
      }
      50% { 
        transform: translateX(-4px) scale(1.02);
        box-shadow: 0 0 25px rgba(34, 197, 94, 0.6);
      }
    }
  }

  @media (min-width: 851px) {
    font-size: 24px;
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.3));
    border: 1px solid rgba(255, 255, 255, 0.2);
  }
`;

const InstructionIcon = styled.span`
  display: block;
  background: linear-gradient(45deg, #3b82f6, #1d4ed8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 900;
  
  @media (max-width: 850px) {
    background: linear-gradient(45deg, #22c55e, #16a34a);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #2d3748;
  z-index: 10;
  transition: opacity 0.5s ease;
  overflow: hidden;
`;

const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 40px;
  
  @media (max-width: 850px) {
    gap: 30px;
  }
`;

const LogoContainer = styled.div`
  width: 240px;
  height: 240px;
  
  @media (max-width: 850px) {
    width: 160px;
    height: 160px;
  }
`;

const LogoSVG = styled.svg`
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1));
`;


// SVG Overlay Component Styles
const SVGOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 3;
  
  /* Ensure it scales with the image container */
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SVGTextOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2; /* Lower z-index so interactive areas are on top */
  
  /* Ensure it scales with the image container */
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SVGContainer = styled.svg<{ panX: number; isDragging?: boolean }>`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: auto;
  
  /* Tablet: Disable pointer events during dragging to allow rotation */
  @media (min-width: 851px) and (max-width: 1300px) {
    pointer-events: ${props => props.isDragging ? 'none' : 'auto'};
    z-index: 1;
  }
  
  /* Mobile panning support - match image behavior exactly */
  @media (max-width: 850px) {
    width: 100%;
    height: 100%;
    left: 0;
    transform: none;
    pointer-events: ${props => props.isDragging ? 'none' : 'auto'};
  }
`;

// Real View SVG Container - behave exactly like the image at all screen sizes
const RealViewSVGContainer = styled.svg<{ panX: number; isDragging?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: auto;
  
  /* Tablet: Disable pointer events during dragging to allow rotation */
  @media (min-width: 851px) and (max-width: 1300px) {
    pointer-events: ${props => props.isDragging ? 'none' : 'auto'};
    z-index: 1;
  }
  
  /* Desktop: Match object-fit: cover behavior */
  @media (min-width: 851px) {
    /* SVG coordinates (5472x3078) match image pixels exactly */
    /* preserveAspectRatio="xMidYMid slice" = object-fit: cover */
  }
  
  /* Mobile: Match object-fit: contain behavior */
  @media (max-width: 850px) {
    width: 100%;
    height: 100%;
    left: 0;
    transform: none;
    pointer-events: ${props => props.isDragging ? 'none' : 'auto'};
  }
`;

// Custom ApartmentPath component with hover logic
const ApartmentPath: React.FC<{
  d: string;
  apartmentId: string;
  hoveredApartmentId: string | null;
  selectedApartmentIds?: string[];
  apartmentColor?: string;
  baseColor?: string;
  isMobile?: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}> = ({ 
  d, 
  apartmentId, 
  hoveredApartmentId, 
  selectedApartmentIds,
  apartmentColor, 
  baseColor, 
  isMobile = false,
  onClick, 
  onMouseEnter, 
  onMouseLeave 
}) => {
  const isHovered = hoveredApartmentId === apartmentId;
  const isSelected = selectedApartmentIds?.includes(apartmentId) || false;
  
  // On mobile: skip hover state (Zustand 2), only show when selected (Zustand 3)
  // On desktop: show both hover and selected states
  const isVisible = isMobile ? isSelected : (isHovered || isSelected);
  
  // Use apartment color (green for available, red for reserved) with some transparency
  const fillColor = apartmentColor ? `${apartmentColor}4D` : 'rgba(59, 130, 246, 0.3)'; // 30% opacity (hover)
  const strokeColor = '#ffffff'; // Always white stroke
  
  // Low opacity when selected (8%)
  const selectedFillColor = apartmentColor ? `${apartmentColor}14` : 'rgba(34, 197, 94, 0.08)'; // 8% opacity (selected)
  
  return (
    <path
      d={d}
      fill={isVisible ? (isSelected ? selectedFillColor : fillColor) : 'transparent'}
      stroke={isVisible ? strokeColor : 'transparent'}
      strokeWidth={isVisible ? 3 : 2}
      opacity={isVisible ? 1 : 0}
      cursor="pointer"
      pointerEvents="auto"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-apartment-id={apartmentId}
      style={{
        filter: isVisible ? 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))' : 'none'
      }}
    />
  );
};

// Custom ApartmentText component with inverse hover logic (visible by default, hidden on hover)
const ApartmentText: React.FC<{
  x: string;
  y: string;
  textContent: string;
  apartmentId: string;
  hoveredApartmentId: string | null;
  fontFamily?: string;
  fontSize?: string;
  fill?: string;
  fillOpacity?: number;
  textAnchor?: string;
}> = ({ 
  x, 
  y, 
  textContent, 
  apartmentId, 
  hoveredApartmentId, 
  fontFamily, 
  fontSize, 
  fill, 
  fillOpacity,
  textAnchor 
}) => {
  // Inverse logic: visible by default, hidden when this apartment is hovered
  const isHidden = hoveredApartmentId === apartmentId;
  
  return (
    <text
      x={x}
      y={y}
      fontFamily={fontFamily || 'Arial, sans-serif'}
      fontSize={fontSize || '14px'}
      fill={fill || "white"}
      fillOpacity={fillOpacity || 1}
      textAnchor={textAnchor || 'middle'}
      opacity={isHidden ? 0 : 1}
      pointerEvents="none"
      style={{
        userSelect: 'none'
      }}
    >
      {textContent}
    </text>
  );
};

// Old loading components removed

const ArrowButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${props => props.direction === 'left' ? 'left: 20px;' : 'right: 20px;'}
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
  backdrop-filter: blur(5px);

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: translateY(-50%) scale(1.1);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: translateY(-50%);
  }

  /* Hide on mobile in favor of slider controls */
  @media ${mediaQueries.mobile} {
    display: none;
  }

  /* Tablet: Same as desktop - no special styling needed */
`;

// Modern Circular Rotation Hook
const useCircularRotation = (
  totalImages: number, 
  currentIndex: number,
  onIndexChange: (index: number) => void,
  onRotationChange?: (isRotating: boolean) => void,
  onTouchStateChange?: (isTouching: boolean) => void,
  isRealView: boolean = false,
  isMobile: boolean = false
) => {
  const [isDragging, setIsDragging] = useState(false);
  const [realTimeAngle, setRealTimeAngle] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastAngle = useRef(0);
  const accumulatedAngle = useRef(0);
  const isDraggingRef = useRef(false);
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;

  const baseAngle = (currentIndex / totalImages) * 360;
  const currentAngle = isDragging ? realTimeAngle : baseAngle;
  const degreesPerFrame = 360 / totalImages;

  const getAngleFromPosition = (clientX: number, clientY: number) => {
    if (!containerRef.current) return 0;
    
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const deltaX = clientX - centerX;
    const deltaY = clientY - centerY;
    
    let angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI) + 90;
    if (angle < 0) angle += 360;
    
    return angle;
  };

  const processAngleMove = (clientX: number, clientY: number) => {
    const newAngle = getAngleFromPosition(clientX, clientY);
    let angleDiff = newAngle - lastAngle.current;
    
    if (angleDiff > 180) angleDiff -= 360;
    if (angleDiff < -180) angleDiff += 360;
    
    setRealTimeAngle(newAngle);
    lastAngle.current = newAngle;
    accumulatedAngle.current += angleDiff;

    const stepThreshold = degreesPerFrame * (isMobile ? 0.35 : 0.5);
    if (Math.abs(accumulatedAngle.current) >= stepThreshold) {
      const steps = Math.trunc(accumulatedAngle.current / degreesPerFrame);
      if (steps !== 0) {
        accumulatedAngle.current -= steps * degreesPerFrame;
        const nextIndex = ((currentIndexRef.current + steps) % totalImages + totalImages) % totalImages;
        onIndexChange(nextIndex);
      }
    }
  };

  const handleStart = (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    isDraggingRef.current = true;
    setIsDragging(true);
    accumulatedAngle.current = 0;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    
    const startAngle = getAngleFromPosition(clientX, clientY);
    lastAngle.current = startAngle;
    setRealTimeAngle(startAngle);
    onRotationChange?.(true);
    onTouchStateChange?.(true);
  };

  const handleMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    processAngleMove(clientX, clientY);
  };

  const handleEnd = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);
    accumulatedAngle.current = 0;
    setRealTimeAngle(baseAngle);
    onTouchStateChange?.(false);
    onRotationChange?.(false);
  };

  useEffect(() => {
    if (!isDragging) return;

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length > 0) {
        processAngleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };
    const onTouchEnd = () => handleEnd();
    const onMouseMove = (e: MouseEvent) => processAngleMove(e.clientX, e.clientY);
    const onMouseUp = () => handleEnd();

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, totalImages, isMobile, isRealView]);

  return {
    containerRef,
    isDragging,
    currentAngle,
    progress: currentIndex / totalImages,
    handleStart,
    handleMove,
    handleEnd
  };
};

// SVG Overlay Component
const SVGOverlayComponent: React.FC<{
  currentIndex: number;
  panX: number;
  isMobile: boolean;
  onApartmentClick?: (apartmentId: string) => void;
  onApartmentHover?: (apartmentId: string | null) => void;
  selectedApartments?: string[];
  apartmentColors?: Record<string, string>;
  hoveredApartment: string | null;
  selectedApartmentsSVG?: string[];
  apartments?: any[];
  isDragging?: boolean;
  isMultiSelectMode?: boolean;
}> = ({ 
  currentIndex, 
  panX, 
  isMobile, 
  onApartmentClick, 
  onApartmentHover,
  selectedApartments = [],
  apartmentColors = {},
  hoveredApartment,
  selectedApartmentsSVG,
  apartments = [],
  isDragging = false,
  isMultiSelectMode = false
}) => {
  const [svgData, setSvgData] = useState<SVGPathsData | null>(null);

  // Load SVG paths data
  useEffect(() => {
    const loadSVGData = async () => {
      try {
        const response = await fetch('/SVG_Paths.json');
        const data: SVGPathsData = await response.json();
        setSvgData(data);
      } catch (error) {
        console.error('Error loading SVG paths:', error);
      }
    };

    loadSVGData();
  }, []);

  // Get frame key with zero-padding - 25 Bilder auf 200 SVG-Frames gemappt
  const getFrameKey = (index: number): string => {
    // 25 images mapped to 200 SVG frames (Index 0→Frame 1, Index 1→Frame 9, etc.)
    const frameNumber = (index * 8) + 1;
    return frameNumber.toString().padStart(4, '0');
  };

  const handleApartmentClick = (apartmentId: string) => {
    onApartmentClick?.(apartmentId);
  };

  const handleApartmentHover = (apartmentId: string | null) => {
    // Don't process hover events during dragging/rotation
    if (isDragging) return;
    onApartmentHover?.(apartmentId);
  };

  // Mapping function to convert SVG apartment IDs to data IDs
  const mapSVGIdToDataId = (svgId: string): number | null => {
    const match = svgId.match(/^([AB])(\d+)$/);
    if (!match) return null;
    
    const [, building, apartmentNum] = match;
    const num = parseInt(apartmentNum, 10);
    
    if (building === 'A') {
      return num; // A1-A11 maps to IDs 1-11
    } else if (building === 'B') {
      return num + 11; // B1-B11 maps to IDs 12-22
    }
    
    return null;
  };

  // Check if apartment should be visible based on filtered apartments
  const isApartmentAvailable = (apartmentId: string): boolean => {
    const dataId = mapSVGIdToDataId(apartmentId);
    return dataId ? apartments.some(apt => apt.id === dataId) : false;
  };

  if (!svgData) return null;

  const frameKey = getFrameKey(currentIndex);
  const currentFrame = svgData.frames[frameKey];

  if (!currentFrame || !currentFrame.apartments) return null;

  // Get all paths from all apartments in the current frame
  const allPaths: Array<{ apartmentId: string; pathData: SVGPathData; pathIndex: number }> = [];
  
  Object.entries(currentFrame.apartments).forEach(([apartmentId, apartment]) => {
    if (apartment.paths && apartment.paths.length > 0) {
      apartment.paths.forEach((pathData, pathIndex) => {
        allPaths.push({ apartmentId, pathData, pathIndex });
      });
    }
  });

  // Filter paths based on:
  // 1. Available/filtered apartments (from filter criteria)
  // 2. Selected apartments (from SVG selection) - BUT in multi-select mode, show all available
  // 3. On mobile, always show all available apartments for clickability
  const visiblePaths = allPaths.filter(({ apartmentId }) => {
    // First check if apartment is in filtered list
    const isInFilteredApartments = isApartmentAvailable(apartmentId);
    if (!isInFilteredApartments) return false;
    
    // On mobile: always show all available apartments (for clickability)
    if (isMobile) {
      return true;
    }
    
    // In multi-select mode, show all available apartments regardless of selection
    if (isMultiSelectMode) {
      return true;
    }
    
    // In normal mode, check SVG selection - if any apartments are selected, only show those
    if (selectedApartments.length > 0) {
      return selectedApartments.includes(apartmentId);
    }
    
    // If no SVG selection and apartment is in filtered list, show it
    return true;
  });

  return (
    <SVGOverlay>
      <SVGContainer 
        panX={isMobile ? panX : 0}
        isDragging={isDragging}
        viewBox="0 0 1920 1080"
        preserveAspectRatio={isMobile ? "xMidYMid meet" : "xMidYMid slice"}
      >
        {visiblePaths.map(({ apartmentId, pathData, pathIndex }) => {
          const apartmentColor = apartmentColors[apartmentId];
          const pathKey = `${frameKey}_${apartmentId}_${pathIndex}`;
          
          return (
            <ApartmentPath
              key={pathKey}
              d={pathData.path_data}
              apartmentColor={apartmentColor}
              baseColor={pathData.fill}
              apartmentId={apartmentId}
              hoveredApartmentId={hoveredApartment}
              selectedApartmentIds={selectedApartments}
              isMobile={isMobile}
              onClick={() => handleApartmentClick(apartmentId)}
              onMouseEnter={() => !isMobile && handleApartmentHover(apartmentId)}
              onMouseLeave={() => !isMobile && handleApartmentHover(null)}
            />
          );
        })}
      </SVGContainer>
    </SVGOverlay>
  );
};

// Real View SVG Overlay Component - 25-frame real view
const RealViewSVGOverlayComponent: React.FC<{
  currentIndex: number;
  panX: number;
  isMobile: boolean;
  onApartmentClick?: (apartmentId: string) => void;
  onApartmentHover?: (apartmentId: string | null) => void;
  selectedApartments?: string[];
  apartmentColors?: Record<string, string>;
  hoveredApartment: string | null;
  selectedApartmentsSVG?: string[];
  apartments?: any[];
  isDragging?: boolean;
  isMultiSelectMode?: boolean;
}> = ({ 
  currentIndex, 
  panX, 
  isMobile, 
  onApartmentClick, 
  onApartmentHover,
  selectedApartments = [],
  apartmentColors = {},
  hoveredApartment,
  selectedApartmentsSVG,
  apartments = [],
  isDragging = false,
  isMultiSelectMode = false
}) => {
  const [svgData, setSvgData] = useState<SVGPathsData | null>(null);


  // Load Real View SVG paths data
  useEffect(() => {
    const loadSVGData = async () => {
      try {
        const response = await fetch('/SVG_Paths_Real.json');
        const data: SVGPathsData = await response.json();
        setSvgData(data);
      } catch (error) {
        console.error('Error loading Real View SVG paths:', error);
      }
    };

    loadSVGData();
  }, []);

  // Get frame key - 25 Bilder auf 100 SVG-Frames gemappt (fehlende Frames: 3, 11, 48)
  const getFrameKey = (index: number): string => {
    const originalIndex = index * 4;
    const excludedFrames = [3, 11, 48];
    let frameNumber = originalIndex + 1;

    for (const excludedFrame of excludedFrames) {
      if (frameNumber >= excludedFrame) {
        frameNumber++;
      }
    }

    return frameNumber.toString().padStart(4, '0');
  };

  const handleApartmentClick = (apartmentId: string) => {
    onApartmentClick?.(apartmentId);
  };

  const handleApartmentHover = (apartmentId: string | null) => {
    // Don't process hover events during dragging/rotation
    if (isDragging) return;
    onApartmentHover?.(apartmentId);
  };

  // Mapping function to convert SVG apartment IDs to data IDs
  const mapSVGIdToDataId = (svgId: string): number | null => {
    const match = svgId.match(/^([AB])(\d+)$/);
    if (!match) return null;
    
    const [, building, apartmentNum] = match;
    const num = parseInt(apartmentNum, 10);
    
    if (building === 'A') {
      return num; // A1-A11 maps to IDs 1-11
    } else if (building === 'B') {
      return num + 11; // B1-B11 maps to IDs 12-22
    }
    
    return null;
  };

  // Check if apartment should be visible based on filtered apartments
  const isApartmentAvailable = (apartmentId: string): boolean => {
    const dataId = mapSVGIdToDataId(apartmentId);
    return dataId ? apartments.some(apt => apt.id === dataId) : false;
  };

  if (!svgData) return null;

  const frameKey = getFrameKey(currentIndex);
  const currentFrame = svgData.frames[frameKey];

  if (!currentFrame || !currentFrame.apartments) return null;

  // Get all paths from all apartments in the current frame
  const allPaths: Array<{ apartmentId: string; pathData: SVGPathData; pathIndex: number }> = [];
  
  Object.entries(currentFrame.apartments).forEach(([apartmentId, apartment]) => {
    if (apartment.paths && apartment.paths.length > 0) {
      apartment.paths.forEach((pathData, pathIndex) => {
        allPaths.push({ apartmentId, pathData, pathIndex });
      });
    }
  });

  // Filter paths based on:
  // 1. Available/filtered apartments (from filter criteria)
  // 2. Selected apartments (from SVG selection) - BUT in multi-select mode, show all available
  // 3. On mobile, always show all available apartments for clickability
  const visiblePaths = allPaths.filter(({ apartmentId }) => {
    // First check if apartment is in filtered list
    const isInFilteredApartments = isApartmentAvailable(apartmentId);
    if (!isInFilteredApartments) return false;
    
    // On mobile: always show all available apartments (for clickability)
    if (isMobile) {
      return true;
    }
    
    // In multi-select mode, show all available apartments regardless of selection
    if (isMultiSelectMode) {
      return true;
    }
    
    // In normal mode, check SVG selection - if any apartments are selected, only show those
    if (selectedApartments.length > 0) {
      return selectedApartments.includes(apartmentId);
    }
    
    // If no SVG selection and apartment is in filtered list, show it
    return true;
  });

  return (
    <SVGOverlay>
      <RealViewSVGContainer 
        panX={isMobile ? panX : 0}
        isDragging={isDragging}
        viewBox="0 0 5472 3078"
        preserveAspectRatio={isMobile ? "xMidYMid meet" : "xMidYMid slice"}
      >
        {visiblePaths.map(({ apartmentId, pathData, pathIndex }) => {
          const apartmentColor = apartmentColors[apartmentId];
          const pathKey = `${frameKey}_${apartmentId}_${pathIndex}`;
          
          return (
            <ApartmentPath
              key={pathKey}
              d={pathData.path_data}
              apartmentColor={apartmentColor}
              baseColor={pathData.fill}
              apartmentId={apartmentId}
              hoveredApartmentId={hoveredApartment}
              selectedApartmentIds={selectedApartments}
              isMobile={isMobile}
              onClick={() => handleApartmentClick(apartmentId)}
              onMouseEnter={() => !isMobile && handleApartmentHover(apartmentId)}
              onMouseLeave={() => !isMobile && handleApartmentHover(null)}
            />
          );
        })}
      </RealViewSVGContainer>
    </SVGOverlay>
  );
};

// SVG Text Overlay Component - Shows text by default, hides on apartment hover
const SVGTextOverlayComponent: React.FC<{
  currentIndex: number;
  panX: number;
  isMobile: boolean;
  hoveredApartmentId: string | null;
  selectedApartments?: string[];
  apartmentColors?: Record<string, string>;
  apartments?: any[];
  isMultiSelectMode?: boolean;
}> = ({ 
  currentIndex, 
  panX, 
  isMobile, 
  hoveredApartmentId,
  selectedApartments = [],
  apartmentColors = {},
  apartments = [],
  isMultiSelectMode = false
}) => {
  // State for different status-based SVG text data
  const [svgTextDataAvailable, setSvgTextDataAvailable] = useState<SVGTextPathsData | null>(null);
  const [svgTextDataReserved, setSvgTextDataReserved] = useState<SVGTextPathsData | null>(null);
  const [svgTextDataSold, setSvgTextDataSold] = useState<SVGTextPathsData | null>(null);

  // Load all SVG text paths data with retry logic
  useEffect(() => {
    const loadSVGTextData = async (filename: string, setter: (data: SVGTextPathsData | null) => void, retryCount = 0) => {
      try {
        console.log(`🔄 Loading ${filename}...`, retryCount > 0 ? `(retry ${retryCount})` : '');
        const startTime = performance.now();
        
        const response = await fetch(`/${filename}`, {
          cache: 'no-cache', // Disable cache to ensure fresh load for debugging
          signal: AbortSignal.timeout(30000) // 30 second timeout for large files
        });
        
        console.log(`📡 Response for ${filename}:`, {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          headers: {
            contentType: response.headers.get('content-type'),
            contentLength: response.headers.get('content-length')
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }
        
        const data: SVGTextPathsData = await response.json();
        const loadTime = performance.now() - startTime;
        
        console.log(`✅ ${filename} loaded successfully in ${loadTime.toFixed(2)}ms:`, {
          availableFrames: Object.keys(data).slice(0, 5),
          totalFrames: Object.keys(data).length,
          sampleFrame: data['0003'] ? Object.keys(data['0003']) : [],
          dataSize: JSON.stringify(data).length
        });
        setter(data);
      } catch (error) {
        console.error(`❌ Error loading ${filename}:`, error);
        
        // Retry up to 2 times with delay
        if (retryCount < 2) {
          console.log(`🔄 Retrying ${filename} load in ${(retryCount + 1) * 1000}ms...`);
          setTimeout(() => {
            loadSVGTextData(filename, setter, retryCount + 1);
          }, (retryCount + 1) * 1000);
        } else {
          console.error(`💥 Failed to load ${filename} after ${retryCount + 1} attempts`);
          setter(null);
        }
      }
    };

    // Load all text path files with additional debugging
    console.log('🚀 Starting to load SVG text files for normal viewer...');
    
    // Load available data with priority (try multiple times if needed)
    const loadAvailableData = async () => {
      try {
        await loadSVGTextData('SVG_Text_Paths.json', setSvgTextDataAvailable);
      } catch (error) {
        console.error('❌ Failed to load SVG_Text_Paths.json, retrying with different approach...');
        // Try loading with different cache settings
        try {
          const response = await fetch('/SVG_Text_Paths.json', { cache: 'default' });
          if (response.ok) {
            const data = await response.json();
            setSvgTextDataAvailable(data);
            console.log('✅ Successfully loaded SVG_Text_Paths.json with fallback method');
          }
        } catch (fallbackError) {
          console.error('❌ Fallback loading also failed:', fallbackError);
        }
      }
    };
    
    loadAvailableData();
    loadSVGTextData('SVG_Text_Reserviert.json', setSvgTextDataReserved);
    loadSVGTextData('SVG_Text_Verkauft.json', setSvgTextDataSold);
    
    // Debug: Log which files are being loaded
    console.log('🔍 Loading SVG text files for normal viewer:', {
      available: 'SVG_Text_Paths.json',
      reserved: 'SVG_Text_Reserviert.json',
      sold: 'SVG_Text_Verkauft.json'
    });
  }, []); // Keep empty dependency array to only load once

  // Function to get the appropriate SVG text data based on apartment status
  const getSVGTextDataForStatus = (status: string): SVGTextPathsData | null => {
    switch (status) {
      case 'available':
        return svgTextDataAvailable;
      case 'reserved':
        return svgTextDataReserved;
      case 'sold':
        return svgTextDataSold;
      default:
        return svgTextDataAvailable; // Fallback to available
    }
  };

  // Get frame key with zero-padding - 25 Bilder auf 200 SVG-Frames gemappt
  const getFrameKey = (index: number): string => {
    // 25 images mapped to 200 SVG frames (Index 0→Frame 1, Index 1→Frame 9, etc.)
    const frameNumber = (index * 8) + 1;
    return frameNumber.toString().padStart(4, '0');
  };

  // Mapping function to convert SVG apartment IDs to data IDs
  const mapSVGIdToDataId = (svgId: string): number | null => {
    const match = svgId.match(/^([AB])(\d+)$/);
    if (!match) return null;
    
    const [, building, apartmentNum] = match;
    const num = parseInt(apartmentNum, 10);
    
    if (building === 'A') {
      return num; // A1-A11 maps to IDs 1-11
    } else if (building === 'B') {
      return num + 11; // B1-B11 maps to IDs 12-22
    }
    
    return null;
  };

  // Get apartment availability and color
  const getApartmentStyle = (apartmentId: string) => {
    const dataId = mapSVGIdToDataId(apartmentId);
    const apartment = apartments.find(apt => apt.id === dataId);
    
    if (apartment) {
      // Get apartment status with backward compatibility
      const getApartmentStatus = (apt: any) => {
        if (apt.status) {
          return apt.status;
        }
        // Backward compatibility: convert available boolean to status
        return apt.available !== false ? 'available' : 'sold';
      };

      const status = getApartmentStatus(apartment);
      
      // Color mapping based on status for SVG text
      switch (status) {
        case 'available':
          return { fill: 'white', fillOpacity: 1 };
        case 'reserved':
          return { fill: '#6b7280', fillOpacity: 1 }; // Medium gray for reserved
        case 'sold':
          return { fill: '#6b7280', fillOpacity: 1 }; // Medium gray for sold
        default:
          return { fill: 'white', fillOpacity: 1 };
      }
    }
    
    // Fallback: white
    return { fill: 'white', fillOpacity: 1 };
  };

  const frameKey = getFrameKey(currentIndex);

  if (!frameKey) {
    return null;
  }

  // Get all texts for current frame from all status-based data sources
  const allTexts: Array<{ apartmentId: string; textData: SVGTextData; textIndex: number }> = [];
  
  // Helper function to get apartment status
  const getApartmentStatus = (apartmentId: string): string => {
    const dataId = mapSVGIdToDataId(apartmentId);
    const apartment = apartments.find(apt => apt.id === dataId);
    
    if (apartment) {
      console.log(`📊 Apartment ${apartmentId} (ID: ${dataId}) status:`, {
        status: apartment.status,
        available: apartment.available,
        finalStatus: apartment.status || (apartment.available !== false ? 'available' : 'sold')
      });
      
      if (apartment.status) {
        return apartment.status;
      }
      // Backward compatibility: convert available boolean to status
      return apartment.available !== false ? 'available' : 'sold';
    }
    
    console.log(`❌ Apartment ${apartmentId} not found in apartments array`);
    return 'available'; // Default fallback
  };

  // Process each apartment individually with its appropriate status-based data
  const processedApartments = new Set<string>();
  
  // Check all possible data sources for apartments in the current frame
  const dataSources = [
    { data: svgTextDataAvailable, status: 'available' },
    { data: svgTextDataReserved, status: 'reserved' },
    { data: svgTextDataSold, status: 'sold' }
  ];

  console.log(`📋 Normal viewer data source status for frame ${frameKey}:`, {
    available: svgTextDataAvailable ? 'loaded' : 'not loaded',
    reserved: svgTextDataReserved ? 'loaded' : 'not loaded', 
    sold: svgTextDataSold ? 'loaded' : 'not loaded'
  });

  // Additional debugging: Check if available data has frame 0021
  if (svgTextDataAvailable) {
    console.log(`🔍 Available data frame keys:`, Object.keys(svgTextDataAvailable).slice(0, 10), '...total:', Object.keys(svgTextDataAvailable).length);
    if (svgTextDataAvailable[frameKey]) {
      console.log(`✅ Frame ${frameKey} found in available data with apartments:`, Object.keys(svgTextDataAvailable[frameKey]));
    } else {
      console.log(`❌ Frame ${frameKey} NOT found in available data`);
    }
  } else {
    console.log(`❌ Available SVG text data is null/undefined`);
  }

  for (const { data, status } of dataSources) {
    if (data && data[frameKey]) {
      console.log(`🔍 Frame ${frameKey} in ${status} data has apartments:`, Object.keys(data[frameKey]));
      
      for (const [apartmentId, textPaths] of Object.entries(data[frameKey])) {
        // Only process each apartment once, using the correct status data
        if (!processedApartments.has(apartmentId)) {
          const apartmentStatus = getApartmentStatus(apartmentId);
          
                  // Only use this data source if it matches the apartment's actual status
        if (apartmentStatus === status) {
          processedApartments.add(apartmentId);
          console.log(`🎯 Using ${status} SVG text data for apartment ${apartmentId} (status: ${apartmentStatus}, ${Array.isArray(textPaths) ? textPaths.length : 0} paths)`);
          
          if (Array.isArray(textPaths)) {
            textPaths.forEach((textData: SVGTextData, index: number) => {
              allTexts.push({
                apartmentId,
                textData,
                textIndex: index
              });
            });
          }
        } else {
          console.log(`⏭️ Skipping ${status} data for apartment ${apartmentId} (actual status: ${apartmentStatus})`);
        }
        }
      }
    } else {
      console.log(`❌ No data for frame ${frameKey} in ${status} source`);
    }
  }

  // Check if apartment should be visible based on filtered apartments
  const isApartmentAvailable = (apartmentId: string): boolean => {
    const dataId = mapSVGIdToDataId(apartmentId);
    return dataId ? apartments.some(apt => apt.id === dataId) : false;
  };

  // Filter texts based on:
  // 1. Available/filtered apartments (from filter criteria)
  // 2. Selected apartments (from SVG selection) - BUT in multi-select mode, show all available
  const visibleTexts = allTexts.filter(({ apartmentId }) => {
    // First check if apartment is in filtered list
    const isInFilteredApartments = isApartmentAvailable(apartmentId);
    if (!isInFilteredApartments) return false;
    
    // In multi-select mode, show all available apartments regardless of selection
    if (isMultiSelectMode) {
      return true;
    }
    
    // In normal mode, check SVG selection - if any apartments are selected, only show those
    if (selectedApartments.length > 0) {
      return selectedApartments.includes(apartmentId);
    }
    
    // If no SVG selection and apartment is in filtered list, show it
    return true;
  });

    return (
    <SVGTextOverlay>
      <SVGContainer 
        panX={isMobile ? panX : 0}
        isDragging={false}
        viewBox="0 0 5472 3078"
        preserveAspectRatio={isMobile ? "xMidYMid meet" : "xMidYMid slice"}
      >
        {visibleTexts.map(({ apartmentId, textData, textIndex }) => {
        const textKey = `${frameKey}_${apartmentId}_text_${textIndex}`;
        
        // Check if this is actually path data (SVG paths) instead of text data
        if (textData && typeof textData === 'object' && textData.d) {
          // This is path data, render as path with availability-based color
          const apartmentStyle = getApartmentStyle(apartmentId);
          
          // Get apartment status to determine stroke behavior
          const dataId = mapSVGIdToDataId(apartmentId);
          const apartment = apartments.find(apt => apt.id === dataId);
          const apartmentStatus = apartment?.status || (apartment?.available !== false ? 'available' : 'sold');
          
          // Always use black stroke for text SVGs
          const strokeValue = '#000000';
          
          // Determine data source and apply selective scaling
          const isFromAvailableData = svgTextDataAvailable && svgTextDataAvailable[frameKey] && svgTextDataAvailable[frameKey][apartmentId];
          let finalPath = textData.d;
          
          // Only scale SVG_Text_Paths.json data (normal apartment texts) from 1920x1080 to 5472x3078
          if (isFromAvailableData && apartmentStatus === 'available') {
            console.log(`🔧 Scaling SVG_Text_Paths.json data for apartment ${apartmentId} from 1920x1080 to 5472x3078`);
            const scaleX = 5472 / 1920;
            const scaleY = 3078 / 1080;
            
            finalPath = textData.d.replace(/([0-9.-]+),?\s*([0-9.-]+)/g, (match, x, y) => {
              const scaledX = parseFloat(x) * scaleX;
              const scaledY = parseFloat(y) * scaleY;
              return `${scaledX.toFixed(3)}, ${scaledY.toFixed(3)}`;
            });
          } else {
            console.log(`✅ Using original coordinates for ${apartmentStatus} apartment ${apartmentId} (already 5472x3078)`);
          }
          
          return (
            <path
              key={textKey}
              d={finalPath}
              fill={apartmentStyle.fill}
              stroke={strokeValue}
              strokeWidth="1"
              fillOpacity={apartmentStyle.fillOpacity}
              fillRule={textData.fill_rule as 'evenodd' | 'nonzero' || 'evenodd'}
              opacity={hoveredApartmentId === apartmentId ? 0 : 1}
              pointerEvents="none"
              style={{
                userSelect: 'none'
              }}
            />
          );
        }
        
        // Otherwise render as text (fallback) with availability-based color
        const apartmentStyle = getApartmentStyle(apartmentId);
        return (
          <ApartmentText
            key={textKey}
            x={textData.x || '0'}
            y={textData.y || '0'}
            textContent={textData.text_content || `Wohnung ${apartmentId}`}
            apartmentId={apartmentId}
            hoveredApartmentId={hoveredApartmentId}
            fontFamily={textData.font_family}
            fontSize={textData.font_size}
            fill={apartmentStyle.fill}
            fillOpacity={apartmentStyle.fillOpacity}
            textAnchor={textData.text_anchor}
          />
        );
      })}
      </SVGContainer>
    </SVGTextOverlay>
  );
};

// Real View SVG Text Overlay Component - 25-frame real view text overlays
const RealViewSVGTextOverlayComponent: React.FC<{
  currentIndex: number;
  panX: number;
  isMobile: boolean;
  hoveredApartmentId: string | null;
  selectedApartments?: string[];
  apartmentColors?: Record<string, string>;
  apartments?: any[];
  isMultiSelectMode?: boolean;
}> = ({ 
  currentIndex, 
  panX, 
  isMobile, 
  hoveredApartmentId,
  selectedApartments = [],
  apartmentColors = {},
  apartments = [],
  isMultiSelectMode = false
}) => {
  // State for different status-based SVG text data (Real View)
  const [svgTextDataAvailable, setSvgTextDataAvailable] = useState<SVGTextPathsData | null>(null);
  const [svgTextDataReserved, setSvgTextDataReserved] = useState<SVGTextPathsData | null>(null);
  const [svgTextDataSold, setSvgTextDataSold] = useState<SVGTextPathsData | null>(null);

  // Load all Real View SVG text paths data with retry logic
  useEffect(() => {
    const loadSVGTextData = async (filename: string, setter: (data: SVGTextPathsData | null) => void, retryCount = 0) => {
      try {
        console.log(`Loading ${filename}...`, retryCount > 0 ? `(retry ${retryCount})` : '');
        const response = await fetch(`/${filename}`, {
          cache: 'force-cache' // Use cache to improve performance on mobile view switches
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data: SVGTextPathsData = await response.json();
        console.log(`${filename} loaded successfully:`, {
          availableFrames: Object.keys(data).slice(0, 5),
          totalFrames: Object.keys(data).length,
          sampleFrame: data['0003'] ? Object.keys(data['0003']) : []
        });
        setter(data);
      } catch (error) {
        console.error(`Error loading ${filename}:`, error);
        
        // Retry up to 2 times with delay
        if (retryCount < 2) {
          console.log(`Retrying ${filename} load in ${(retryCount + 1) * 1000}ms...`);
          setTimeout(() => {
            loadSVGTextData(filename, setter, retryCount + 1);
          }, (retryCount + 1) * 1000);
        } else {
          setter(null);
        }
      }
    };

    // Load all Real View text path files
    loadSVGTextData('SVG_Text_Paths_Real.json', setSvgTextDataAvailable);
    loadSVGTextData('SVG_Text_Reserviert_Real.json', setSvgTextDataReserved);
    loadSVGTextData('SVG_Text_Verkauft_Real.json', setSvgTextDataSold);
    
    // Debug: Log which files are being loaded
    console.log('🔍 Loading SVG text files for Real viewer:', {
      available: 'SVG_Text_Paths_Real.json',
      reserved: 'SVG_Text_Reserviert_Real.json',
      sold: 'SVG_Text_Verkauft_Real.json'
    });
  }, []); // Keep empty dependency array to only load once

  // Get frame key - 25 Bilder auf 100 SVG-Frames gemappt (fehlende Frames: 3, 11, 48)
  const getFrameKey = (index: number): string => {
    const originalIndex = index * 4;
    const excludedFrames = [3, 11, 48];
    let frameNumber = originalIndex + 1;

    for (const excludedFrame of excludedFrames) {
      if (frameNumber >= excludedFrame) {
        frameNumber++;
      }
    }

    return frameNumber.toString().padStart(4, '0');
  };

  // Mapping function to convert SVG apartment IDs to data IDs
  const mapSVGIdToDataId = (svgId: string): number | null => {
    const match = svgId.match(/^([AB])(\d+)$/);
    if (!match) return null;
    
    const [, building, apartmentNum] = match;
    const num = parseInt(apartmentNum, 10);
    
    if (building === 'A') {
      return num; // A1-A11 maps to IDs 1-11
    } else if (building === 'B') {
      return num + 11; // B1-B11 maps to IDs 12-22
    }
    
    return null;
  };

  // Get apartment availability and color
  const getApartmentStyle = (apartmentId: string) => {
    const dataId = mapSVGIdToDataId(apartmentId);
    const apartment = apartments.find(apt => apt.id === dataId);
    
    if (apartment) {
      // Get apartment status with backward compatibility
      const getApartmentStatus = (apt: any) => {
        if (apt.status) {
          return apt.status;
        }
        // Backward compatibility: convert available boolean to status
        return apt.available !== false ? 'available' : 'sold';
      };

      const status = getApartmentStatus(apartment);
      
      // Color mapping based on status for SVG text
      switch (status) {
        case 'available':
          return { fill: 'white', fillOpacity: 1 };
        case 'reserved':
          return { fill: '#6b7280', fillOpacity: 1 }; // Medium gray for reserved
        case 'sold':
          return { fill: '#6b7280', fillOpacity: 1 }; // Medium gray for sold
        default:
          return { fill: 'white', fillOpacity: 1 };
      }
    }
    
    // Fallback: white
    return { fill: 'white', fillOpacity: 1 };
  };

  const frameKey = getFrameKey(currentIndex);

  if (!frameKey) {
    return null;
  }

  // Get all texts for current frame from all status-based data sources
  const allTexts: Array<{ apartmentId: string; textData: any; textIndex: number }> = [];
  
  // Helper function to get apartment status
  const getApartmentStatus = (apartmentId: string): string => {
    const dataId = mapSVGIdToDataId(apartmentId);
    const apartment = apartments.find(apt => apt.id === dataId);
    
    if (apartment) {
      console.log(`📊 Real View Apartment ${apartmentId} (ID: ${dataId}) status:`, {
        status: apartment.status,
        available: apartment.available,
        finalStatus: apartment.status || (apartment.available !== false ? 'available' : 'sold')
      });
      
      if (apartment.status) {
        return apartment.status;
      }
      // Backward compatibility: convert available boolean to status
      return apartment.available !== false ? 'available' : 'sold';
    }
    
    console.log(`❌ Real View: Apartment ${apartmentId} not found in apartments array`);
    return 'available'; // Default fallback
  };

  // Process each apartment individually with its appropriate status-based data
  const processedApartments = new Set<string>();
  
  // Check all possible data sources for apartments in the current frame
  const dataSources = [
    { data: svgTextDataAvailable, status: 'available' },
    { data: svgTextDataReserved, status: 'reserved' },
    { data: svgTextDataSold, status: 'sold' }
  ];

  console.log(`📋 Real viewer data source status for frame ${frameKey}:`, {
    available: svgTextDataAvailable ? 'loaded' : 'not loaded',
    reserved: svgTextDataReserved ? 'loaded' : 'not loaded', 
    sold: svgTextDataSold ? 'loaded' : 'not loaded'
  });

  for (const { data, status } of dataSources) {
    if (data && data[frameKey]) {
      console.log(`🔍 Real View Frame ${frameKey} in ${status} data has apartments:`, Object.keys(data[frameKey]));
      
      for (const [apartmentId, textPaths] of Object.entries(data[frameKey])) {
        // Only process each apartment once, using the correct status data
        if (!processedApartments.has(apartmentId)) {
          const apartmentStatus = getApartmentStatus(apartmentId);
          
          // Only use this data source if it matches the apartment's actual status
          if (apartmentStatus === status) {
            processedApartments.add(apartmentId);
            console.log(`🎯 Real View: Using ${status} SVG text data for apartment ${apartmentId}`);
            
            if (Array.isArray(textPaths)) {
              textPaths.forEach((textData: any, index: number) => {
                allTexts.push({
                  apartmentId,
                  textData,
                  textIndex: index
                });
              });
            }
          } else {
            console.log(`⏭️ Real View: Skipping ${status} data for apartment ${apartmentId} (actual status: ${apartmentStatus})`);
          }
        }
      }
    } else {
      console.log(`❌ Real View: No data for frame ${frameKey} in ${status} source`);
    }
  }

  // Check if apartment should be visible based on filtered apartments
  const isApartmentAvailable = (apartmentId: string): boolean => {
    const dataId = mapSVGIdToDataId(apartmentId);
    return dataId ? apartments.some(apt => apt.id === dataId) : false;
  };

  // Filter texts based on available apartments
  const visibleTexts = allTexts.filter(({ apartmentId }) => {
    // First check if apartment is in filtered list
    const isInFilteredApartments = isApartmentAvailable(apartmentId);
    if (!isInFilteredApartments) return false;
    
    // Show all available apartment texts
    return true;
  });

  return (
    <SVGTextOverlay>
      <RealViewSVGContainer 
        panX={isMobile ? panX : 0}
        isDragging={false}
        viewBox="0 0 5472 3078"
        preserveAspectRatio={isMobile ? "xMidYMid meet" : "xMidYMid slice"}
      >
        {visibleTexts.map(({ apartmentId, textData, textIndex }) => {
          const textKey = `${frameKey}_${apartmentId}_text_${textIndex}`;
          
          // Check if this is actually path data (SVG paths) instead of text data
          if (textData && typeof textData === 'object' && textData.d) {
            // This is path data, render as path with availability-based color
            const apartmentStyle = getApartmentStyle(apartmentId);
            
            // Get apartment status to determine stroke behavior
            const dataId = mapSVGIdToDataId(apartmentId);
            const apartment = apartments.find(apt => apt.id === dataId);
            const apartmentStatus = apartment?.status || (apartment?.available !== false ? 'available' : 'sold');
            
            // Use black stroke for all text SVGs in Real Viewer
            const strokeValue = '#000000';
            
            return (
              <path
                key={textKey}
                d={textData.d}
                fill={apartmentStyle.fill}
                stroke={strokeValue}
                strokeWidth="1"
                fillOpacity={apartmentStyle.fillOpacity}
                fillRule={textData.fill_rule as 'evenodd' | 'nonzero' || 'evenodd'}
                opacity={hoveredApartmentId === apartmentId ? 0 : 1}
                pointerEvents="none"
                style={{
                  userSelect: 'none'
                }}
              />
            );
          }
          
          // Otherwise render as text (fallback) with availability-based color
          const apartmentStyle = getApartmentStyle(apartmentId);
          return (
            <ApartmentText
              key={textKey}
              x={textData.x || '0'}
              y={textData.y || '0'}
              textContent={textData.text_content || `Wohnung ${apartmentId}`}
              apartmentId={apartmentId}
              hoveredApartmentId={hoveredApartmentId}
              fontFamily={textData.font_family}
              fontSize={textData.font_size}
              fill={apartmentStyle.fill}
              fillOpacity={apartmentStyle.fillOpacity}
              textAnchor={textData.text_anchor}
            />
                  );
      })}
      </RealViewSVGContainer>
    </SVGTextOverlay>
  );
};

// Tablet Horizontal Scroll Bar Component
const TabletScrollBar: React.FC<{
  totalImages: number;
  currentIndex: number;
  onIndexChange: (index: number) => void;
}> = ({ totalImages, currentIndex, onIndexChange }) => {
  const [isDragging, setIsDragging] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  // Calculate thumb position as percentage
  const thumbPosition = (currentIndex / (totalImages - 1)) * 100;

  const handleTrackClick = (e: React.MouseEvent) => {
    if (!trackRef.current) return;
    
    const rect = trackRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newIndex = Math.round(percentage * (totalImages - 1));
    
    onIndexChange(Math.max(0, Math.min(totalImages - 1, newIndex)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!trackRef.current) return;
      
      const rect = trackRef.current.getBoundingClientRect();
      const moveX = e.clientX - rect.left;
      const percentage = moveX / rect.width;
      const newIndex = Math.round(percentage * (totalImages - 1));
      
      onIndexChange(Math.max(0, Math.min(totalImages - 1, newIndex)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    
    const handleTouchMove = (e: TouchEvent) => {
      if (!trackRef.current) return;
      
      const rect = trackRef.current.getBoundingClientRect();
      const moveX = e.touches[0].clientX - rect.left;
      const percentage = moveX / rect.width;
      const newIndex = Math.round(percentage * (totalImages - 1));
      
      onIndexChange(Math.max(0, Math.min(totalImages - 1, newIndex)));
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  };

  return (
    <TabletScrollBarContainer>
      <TabletScrollBarTrack 
        ref={trackRef}
        onClick={handleTrackClick}
      >
        <TabletScrollBarThumb 
          position={thumbPosition}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        />
      </TabletScrollBarTrack>
    </TabletScrollBarContainer>
  );
};

// Modern Circular Rotation Control Component
const CircularRotationControl: React.FC<{
  totalImages: number;
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onRotationChange?: (isRotating: boolean) => void;
  onTouchStateChange?: (isTouching: boolean) => void;
  isRealView?: boolean;
  isMobile?: boolean;
}> = ({ totalImages, currentIndex, onIndexChange, onRotationChange, onTouchStateChange, isRealView = false, isMobile = false }) => {
  const { 
    containerRef, 
    isDragging, 
    currentAngle,
    progress,
    handleStart,
    handleMove,
    handleEnd
  } = useCircularRotation(totalImages, currentIndex, onIndexChange, onRotationChange, onTouchStateChange, isRealView, isMobile);

  const handleTouchStart = (e: React.TouchEvent) => {
    // Prevent scrolling and zooming during touch interaction
    e.preventDefault();
    handleStart(e);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Prevent scrolling and improve touch responsiveness
    e.preventDefault();
    e.stopPropagation();
    handleMove(e);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleEnd();
  };

  return (
    <CircularRotationContainer>
      <CircularSlider
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onContextMenu={(e) => e.preventDefault()}
      >
        <CircularTrack />
        <CircularSliderProgress progress={progress} isDragging={isDragging} />
        <CircularHandle angle={currentAngle} isDragging={isDragging} />
        
        <CircularCenterDisplay>
          <CircularControlLabel>360°</CircularControlLabel>
          <CircularControlSubtitle>Steuerung</CircularControlSubtitle>
        </CircularCenterDisplay>
      </CircularSlider>
    </CircularRotationContainer>
  );
};



const Viewer360: React.FC<Viewer360Props & { 
  onRotationChange?: (isRotating: boolean) => void;
  onTouchStateChange?: (isTouching: boolean) => void;
  mobileOverlayMenu?: React.ReactNode;
  onApartmentSVGSelect?: (apartmentId: string) => void;
  onApartmentSVGClear?: () => void;
  onMarkedApartmentsChange?: (markedApartments: Set<string>) => void;
  onClearAllMarkings?: () => void;
  isRealView?: boolean;
  textSVGVisible?: boolean;
  onTabletApartmentListClick?: (apartmentId: string) => void;
}> = ({ 
  images, 
  currentIndex, 
  onIndexChange, 
  onRotationChange,
  onTouchStateChange,
  mobileOverlayMenu,
  onApartmentClick,
  onMobileApartmentDetails,
  showMobileBackButton = false,
  onMobileBackToDetails,
  onApartmentHover,
  onTabletApartmentListClick,
  selectedApartments,
  apartmentColors,
  showSVGOverlay = true,
  showTextOverlay = true,
  svgOverlayVisible = true,
  textSVGVisible = false,
  apartments = [],
  externalHoveredApartment = null,
  isMultiSelectMode = false,
  onApartmentSVGSelect,
  onApartmentSVGClear,
  onMarkedApartmentsChange,
  onClearAllMarkings,
  isRealView = false
}) => {
  const [isPreloading, setIsPreloading] = useState(true);
  const [preloadProgress, setPreloadProgress] = useState(0);
  const [countdown, setCountdown] = useState(5);
  
  const [isDragging, setIsDragging] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [panX, setPanX] = useState(0);
  const [maxPan, setMaxPan] = useState(150); // Default value
  const { deviceType, isMobile, isTablet, isDesktop } = useDeviceDetection();
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [hasUsedGreenWheel, setHasUsedGreenWheel] = useState(false); // Track if user has used the green rotation wheel
  const [isCurrentlyRotating, setIsCurrentlyRotating] = useState(false); // Track if currently rotating with green wheel
  const [desktopRotationCount, setDesktopRotationCount] = useState(0); // Track successful rotations on desktop
  const [hoveredApartment, setHoveredApartment] = useState<string | null>(null);

  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // NEW: Mobile touch handling for apartment interactions
  const [touchStartedOnApartment, setTouchStartedOnApartment] = useState(false);
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [touchStartPosition, setTouchStartPosition] = useState({ x: 0, y: 0 });

  // Mobile apartment selection - now handled via tooltip with Details button
  const [selectedApartmentForTooltip, setSelectedApartmentForTooltip] = useState<string | null>(null);
  const [isTooltipPositionedBelow, setIsTooltipPositionedBelow] = useState<boolean>(false);
  
  // Track device orientation for proper touch event handling after rotation
  const [deviceOrientation, setDeviceOrientation] = useState<number>(window.orientation || 0);

  // Combined hover state for SVG highlighting - prioritize direct SVG hover over external hover
  // Disable hover during dragging/rotation
  // On mobile, also show highlighting for selected apartments
  const effectiveHoveredApartmentForSVG = (isDragging || isPanning) ? null : 
    (hoveredApartment || externalHoveredApartment || 
     (isMobile && selectedApartments && selectedApartments.length > 0 ? selectedApartments[0] : null));

  // Separate hover state for tooltips - only use direct SVG hover, not card hover
  // This prevents tooltips from showing when hovering apartment cards
  // TABLET/MOBILE: No floating tooltips (mobile shows info in bottom panel instead)
  const isTabletForTooltip = window.innerWidth > 850 && window.innerWidth <= 1300;
  const effectiveHoveredApartmentForTooltip = (isDragging || isPanning || isTabletForTooltip || isMobile) ? null : 
    hoveredApartment;

  // Debug: Log hover state changes for debugging
  React.useEffect(() => {
    if (externalHoveredApartment) {
      console.log('🎯 External hover apartment:', externalHoveredApartment);
    }
    if (effectiveHoveredApartmentForSVG) {
      console.log('✨ Effective hover apartment for SVG:', effectiveHoveredApartmentForSVG);
    }
    if (effectiveHoveredApartmentForTooltip) {
      console.log('🏷️ Effective hover apartment for Tooltip:', effectiveHoveredApartmentForTooltip);
    }
  }, [externalHoveredApartment, effectiveHoveredApartmentForSVG, effectiveHoveredApartmentForTooltip]);

  // Wrapper function for green wheel rotation to track usage
  const handleGreenWheelRotation = (index: number) => {
    setHasUsedGreenWheel(true);
    onIndexChange(index);
  };

  // Wrapper function for rotation change to track when actively rotating
  const handleRotationChange = (isRotating: boolean) => {
    setIsCurrentlyRotating(isRotating);
    if (onRotationChange) {
      onRotationChange(isRotating);
    }
  };

    const dragStartPos = useRef(0);
  const dragStartIndex = useRef(0);
  const dragStartTime = useRef(0); // Track drag start time
  const totalDragDistance = useRef(0); // Track total drag distance
  const panStartPos = useRef(0);
  const panStartOffset = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showInstructions, setShowInstructions] = useState<boolean>(false);

  // Simple drag tracking for rotation
  const lastDragPos = useRef(0);
  const lastDragTime = useRef(0);

  // Device detection is now handled by useDeviceDetection hook

  // Cleanup momentum animation on unmount
  useEffect(() => {
    return () => {
      if (momentumAnimation.current) {
        cancelAnimationFrame(momentumAnimation.current);
        momentumAnimation.current = null;
      }
    };
  }, []);

  // Handle orientation changes to ensure proper touch event handling after rotation
  useEffect(() => {
    const handleOrientationChange = () => {
      // Update orientation state
      setDeviceOrientation(window.orientation || 0);
      
      // DON'T automatically clear selections on orientation change
      // Instead, let the user manually clear by clicking outside
      // This was the source of the problem - auto-clearing prevented proper deselection
      
      // Small delay to ensure the layout has updated after rotation
      setTimeout(() => {
        // Force a layout recalculation to ensure coordinates are correct
        if (containerRef.current) {
          containerRef.current.getBoundingClientRect();
        }
      }, 150); // Slightly longer delay to ensure rotation is complete
    };

    // Listen for orientation changes
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Also listen for resize events as a fallback
    window.addEventListener('resize', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, []);

  // Smart image preloading with live countdown based on actual loading speed
  useEffect(() => {
    if (!images || images.length === 0) return;
    
    console.log('Starting to preload', images.length, 'images');
    setIsPreloading(true);
    setCountdown(5); // Initial estimate
    setPreloadProgress(0);
    
    let loadedCount = 0;
    const totalImages = images.length;
    const startTime = Date.now();
    let countdownTimer: NodeJS.Timeout;
    
    // Function to calculate and update estimated remaining time
    const updateEstimatedTime = () => {
      const elapsedTime = (Date.now() - startTime) / 1000; // in seconds
      const progressPercent = (loadedCount / totalImages) * 100;
      setPreloadProgress(progressPercent);
      
      if (loadedCount === 0) {
        // No images loaded yet, keep initial estimate
        setCountdown(Math.max(1, 5 - Math.floor(elapsedTime)));
        return;
      }
      
      // Calculate loading rate and estimate remaining time
      const loadingRate = loadedCount / elapsedTime; // images per second
      const remainingImages = totalImages - loadedCount;
      const estimatedRemainingTime = Math.ceil(remainingImages / loadingRate);
      
      // Update countdown with estimated time (minimum 1 second)
      const displayTime = Math.max(1, estimatedRemainingTime);
      setCountdown(displayTime);
      
      console.log(`Progress: ${loadedCount}/${totalImages} (${progressPercent.toFixed(1)}%) - Estimated time remaining: ${displayTime}s`);
      
      // Finish loading when all images are loaded
      if (loadedCount >= totalImages) {
        console.log('All images preloaded successfully');
        clearInterval(countdownTimer);
        setIsPreloading(false);
        setCountdown(0);
      }
    };
    
    // Update countdown every 500ms for smooth updates
    countdownTimer = setInterval(() => {
      updateEstimatedTime();
    }, 500);
    
    // Preload all images
    images.forEach((src, index) => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        console.log(`Image ${index + 1}/${totalImages} loaded`);
        updateEstimatedTime();
      };
      img.onerror = () => {
        console.warn(`Failed to load image ${index + 1}:`, src);
        loadedCount++; // Count failed images as "loaded" to not block the UI
        updateEstimatedTime();
      };
      img.src = src;
    });
    
    // Fallback: Force finish after 30 seconds maximum
    const fallbackTimer = setTimeout(() => {
      console.log('Fallback timer triggered - finishing preload');
      clearInterval(countdownTimer);
      setIsPreloading(false);
      setCountdown(0);
    }, 30000);
    
    return () => {
      clearInterval(countdownTimer);
      clearTimeout(fallbackTimer);
    };
  }, [images]);

  // Instruction visibility logic
  useEffect(() => {
    if (isPreloading) {
      setShowInstructions(false);
      return;
    }

    const initialShowTimer = setTimeout(() => setShowInstructions(true), 100);
    return () => {
      clearTimeout(initialShowTimer);
    };
  }, [isPreloading]);

  // Old loading message and facts logic removed - no longer needed

  // Enhanced touch rotation state for tablets
  const touchVelocity = useRef(0);
  const touchMomentum = useRef(0);
  const momentumAnimation = useRef<number | null>(null);
  const lastTouchTime = useRef(0);
  const touchHistory = useRef<Array<{x: number, time: number}>>([]);

  const onDragStart = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    // NEW: Enhanced touch detection for mobile vs tablet with duration-based gesture recognition
    const isTouch = 'touches' in e;
    const clientX = isTouch ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = isTouch ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    const touchTime = Date.now();
    
    if (isTablet && isTouch) {
      // TABLET TOUCH: Only handle apartment clicks, no rotation (use scroll bar instead)
      // Check if touch started on an SVG apartment element
      const target = document.elementFromPoint(clientX, clientY);
      const isOnApartment = target && (
        target.closest('path[cursor="pointer"]') || 
        target.getAttribute('cursor') === 'pointer' ||
        target.tagName?.toLowerCase() === 'path' ||
        (target as any).style?.cursor === 'pointer'
      );
      
      setTouchStartedOnApartment(!!isOnApartment);
      setTouchStartTime(touchTime);
      setTouchStartPosition({ x: clientX, y: clientY });
      
      // If touch started on apartment area, handle as potential tap
      if (isOnApartment) {
        // Don't start any rotation - only handle apartment clicks
        return;
      } else {
        // Touch started outside apartment area - no rotation, just ignore
        setTouchStartedOnApartment(false);
        setTouchStartTime(0);
        setTouchStartPosition({ x: 0, y: 0 });
        return; // Exit early, no rotation for tablets
      }
    } else if (isMobile || (isTablet && !isTouch)) {
      // Mobile always, Tablet for mouse events only
      // Check if touch/click started on an SVG apartment element
      const target = document.elementFromPoint(clientX, clientY);
      const isOnApartment = target && (
        target.closest('path[cursor="pointer"]') || 
        target.getAttribute('cursor') === 'pointer' ||
        target.tagName?.toLowerCase() === 'path' ||
        (target as any).style?.cursor === 'pointer'
      );
      
      setTouchStartedOnApartment(!!isOnApartment);
      setTouchStartTime(touchTime);
      setTouchStartPosition({ x: clientX, y: clientY });
      
      // If touch/click started on apartment, don't start panning immediately
      if (isOnApartment) {
        // Mobile: Wait to see if it's a tap or a drag
        if (isMobile) {
          return;
        }
        // Desktop: Brief delay to see if this is a tap or a drag
        if (!isTablet) {
          return;
        }
      }
    }
    
    // Original logic for desktop and non-apartment mobile touches
    e.preventDefault();
    
    const finalClientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const now = performance.now();
    
    if (isMobile) {
      // Mobile: Start rotation via horizontal swipe
      setIsDragging(true);
      dragStartPos.current = finalClientX;
      dragStartIndex.current = currentIndex;
      dragStartTime.current = now;
      totalDragDistance.current = 0;
      lastDragPos.current = finalClientX;
      lastDragTime.current = now;
      setHasUserInteracted(true);
      onRotationChange?.(true);
    } else if (isTablet) {
      // Tablet: Enhanced touch rotation
      console.log('Tablet drag start - setting isDragging to true, startIndex:', currentIndex, 'clientX:', finalClientX);
      setIsDragging(true);
      dragStartPos.current = finalClientX;
      dragStartIndex.current = currentIndex;
      dragStartTime.current = now;
      totalDragDistance.current = 0;
      
      // Reset touch-specific tracking
      touchVelocity.current = 0;
      touchMomentum.current = 0;
      lastTouchTime.current = now;
      touchHistory.current = [{x: finalClientX, time: now}];
      
      // Cancel any existing momentum animation
      if (momentumAnimation.current) {
        cancelAnimationFrame(momentumAnimation.current);
        momentumAnimation.current = null;
      }
      
      onRotationChange?.(true);
      lastDragPos.current = finalClientX;
      lastDragTime.current = now;
    } else if (isDesktop) {
      // Desktop: Standard rotation
      setIsDragging(true);
      dragStartPos.current = finalClientX;
      dragStartIndex.current = currentIndex;
      dragStartTime.current = now;
      totalDragDistance.current = 0;
      onRotationChange?.(true);
      
      lastDragPos.current = finalClientX;
      lastDragTime.current = now;
    }
    
    if (showInstructions) {
      setShowInstructions(false);
    }
  };

  const onDragMove = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const isTouch = 'touches' in e;
    let isActivelyDragging = isDragging || isPanning;
    
    const shouldUseTouchLogic = isMobile || (isTablet && !isTouch);
    
    if (shouldUseTouchLogic && touchStartedOnApartment) {
      const clientX = isTouch ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
      const clientY = isTouch ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
      const currentTime = Date.now();
      const movementDistance = Math.sqrt(
        Math.pow(clientX - touchStartPosition.x, 2) + 
        Math.pow(clientY - touchStartPosition.y, 2)
      );
      
      if (movementDistance > 10 || (currentTime - touchStartTime) > 200) {
        setTouchStartedOnApartment(false);
        setIsDragging(true);
        isActivelyDragging = true;
        dragStartPos.current = clientX;
        dragStartIndex.current = currentIndex;
        dragStartTime.current = performance.now();
        totalDragDistance.current = 0;
        lastDragPos.current = clientX;
        lastDragTime.current = performance.now();
        setHasUserInteracted(true);
        onRotationChange?.(true);
      } else {
        return;
      }
    }
    
    // Handle tablet touch apartment detection - no rotation, only taps
    if (isTablet && isTouch && touchStartedOnApartment) {
      // For tablets, we don't do rotation via touch - only handle as potential taps
      // Movement detection is still useful to distinguish between taps and drags
      const clientX = e.touches[0].clientX;
      const clientY = e.touches[0].clientY;
      const movementDistance = Math.sqrt(
        Math.pow(clientX - touchStartPosition.x, 2) + 
        Math.pow(clientY - touchStartPosition.y, 2)
      );
      
      // If user moves more than 15px, it's no longer a tap
      if (movementDistance > 15) {
        console.log('Movement detected - no longer a tap, ignoring');
        setTouchStartedOnApartment(false);
        // Don't start rotation - tablets use scroll bar for rotation
        return;
      } else {
        // Still within tap threshold, don't process as drag yet
        return;
      }
    }
    
    if (!isActivelyDragging || !containerRef.current) return;
    e.preventDefault();

    const moveClientX = 'touches' in e ? e.touches[0].clientX : e.clientX;

    if (isMobile && isActivelyDragging) {
      const deltaX = moveClientX - lastDragPos.current;
      totalDragDistance.current += Math.abs(deltaX);
      const sensitivity = 1.2;
      const moveDistance = Math.abs(deltaX) * sensitivity;
      const moveThreshold = 2;

      if (deltaX < -moveThreshold) {
        const imagesToSkip = Math.max(1, Math.min(3, Math.floor(moveDistance / 6)));
        const nextIndex = (currentIndex + imagesToSkip) % images.length;
        onIndexChange(nextIndex);
        lastDragPos.current = moveClientX;
        setHasUsedGreenWheel(true);
      } else if (deltaX > moveThreshold) {
        const imagesToSkip = Math.max(1, Math.min(3, Math.floor(moveDistance / 6)));
        const nextIndex = (currentIndex - imagesToSkip + images.length) % images.length;
        onIndexChange(nextIndex);
        lastDragPos.current = moveClientX;
        setHasUsedGreenWheel(true);
      }
    } else if ((isMobile || isTablet) && isPanning) {
      // Handle panning on mobile and tablet (when clicking on apartments)
      const dragDistance = moveClientX - panStartPos.current;
      const newPanX = panStartOffset.current + dragDistance;
      
      // Calculate dynamic panning range based on container and image width
      const containerWidth = containerRef.current.offsetWidth;
      const imageWidth = containerWidth * 3; // 300% of container width
      const calculatedMaxPan = (imageWidth - containerWidth) / 2; // Maximum pan distance
      
      // Update maxPan state if it has changed
      if (calculatedMaxPan !== maxPan) {
        setMaxPan(calculatedMaxPan);
      }
      
      const clampedPanX = Math.max(-calculatedMaxPan, Math.min(calculatedMaxPan, newPanX));
      setPanX(clampedPanX);
    } else if (isTablet && isDragging) {
      // TABLET: No touch rotation - tablets use scroll bar for rotation
      // This block should not be reached since we disabled touch rotation for tablets
      console.log('Tablet rotation disabled - use scroll bar instead');
      return;
      
    } else if (isDesktop && isDragging) {
      // DESKTOP: Direct rotation logic from HausViewer (enhanced sensitivity)
      const deltaX = moveClientX - lastDragPos.current;
      
      // Track total drag distance for instruction logic
      totalDragDistance.current += Math.abs(deltaX);
      
      // Enhanced sensitivity for more responsive rotation
      const sensitivity = 1.4; // Increased for higher sensitivity
      const moveDistance = Math.abs(deltaX) * sensitivity;
      // Use smaller threshold for more immediate feedback
      const moveThreshold = 2; // Reduced from 3 for quicker response
      
      if (deltaX < -moveThreshold) {
        // Nach links ziehen - Bildzahl erhöhen
        const imagesToSkip = Math.max(1, Math.min(4, Math.floor(moveDistance / 8))); // Changed from /10 to /8, max from 3 to 4
        const nextIndex = (currentIndex + imagesToSkip) % images.length;
        onIndexChange(nextIndex);
        lastDragPos.current = moveClientX;
      } else if (deltaX > moveThreshold) {
        // Nach rechts ziehen - Bildzahl verringern
        const imagesToSkip = Math.max(1, Math.min(4, Math.floor(moveDistance / 8))); // Changed from /10 to /8, max from 3 to 4
        const nextIndex = (currentIndex - imagesToSkip + images.length) % images.length;
        onIndexChange(nextIndex);
        lastDragPos.current = moveClientX;
      }
    }
  };

  // Momentum animation function for tablets
  const animateMomentum = (velocity: number, currentIdx: number) => {
    if (Math.abs(velocity) < 0.1) return; // Stop if velocity is too low
    
    const friction = 0.95; // Friction factor
    const minVelocity = 0.1;
    
    // Calculate next frame based on velocity
    const frameStep = velocity > 0 ? -1 : 1; // Invert for natural feel
    let nextIndex = currentIdx + frameStep;
    nextIndex = ((nextIndex % images.length) + images.length) % images.length;
    
    onIndexChange(nextIndex);
    
    // Reduce velocity
    velocity *= friction;
    
    // Continue animation if velocity is still significant
    if (Math.abs(velocity) > minVelocity) {
      momentumAnimation.current = requestAnimationFrame(() => 
        animateMomentum(velocity, nextIndex)
      );
    } else {
      momentumAnimation.current = null;
      onRotationChange?.(false);
    }
  };

  const onDragEnd = (e?: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    // Handle tablet touch taps on apartments (quick taps < 200ms without much movement)
    if (isTablet && touchStartedOnApartment) {
      const currentTime = Date.now();
      const touchDuration = currentTime - touchStartTime;
      
      // Get coordinates from either mouse or touch event
      let clientX: number, clientY: number;
      if (e && 'touches' in e && e.changedTouches.length > 0) {
        // Touch event
        clientX = e.changedTouches[0].clientX;
        clientY = e.changedTouches[0].clientY;
      } else if (e && 'clientX' in e) {
        // Mouse event
        clientX = e.clientX;
        clientY = e.clientY;
      } else {
        // Fallback to stored position
        clientX = touchStartPosition.x;
        clientY = touchStartPosition.y;
      }
      
      const movementDistance = Math.sqrt(
        Math.pow(clientX - touchStartPosition.x, 2) + 
        Math.pow(clientY - touchStartPosition.y, 2)
      );
      
      // Quick tap: < 200ms duration and < 15px movement
      if (touchDuration < 200 && movementDistance < 15) {
        console.log('Quick tap detected - handling apartment click');
        
        // Find the target element at tap position
        let target = document.elementFromPoint(clientX, clientY);
        
        // Fallback if elementFromPoint fails
        if (!target && e) {
          target = e.target as HTMLElement;
        }
        
        // Check if tapping on apartment SVG path
        let clickedApartmentId: string | null = null;
        
        // First try: Check the direct target element
        if (target && target.tagName === 'path' && target.hasAttribute('data-apartment-id')) {
          clickedApartmentId = target.getAttribute('data-apartment-id');
        }
        
        // Second try: Use elementFromPoint to check what's actually under the tap coordinates
        if (!clickedApartmentId) {
          const elementUnderTap = document.elementFromPoint(clientX, clientY);
          if (elementUnderTap && elementUnderTap.tagName === 'path' && elementUnderTap.hasAttribute('data-apartment-id')) {
            clickedApartmentId = elementUnderTap.getAttribute('data-apartment-id');
          }
        }
        
        if (clickedApartmentId) {
          // User tapped on apartment SVG -> Open side panel directly (like the old "Wohnung anzeigen" button)
          console.log('📱 Tablet tap detected, calling onTabletApartmentListClick for:', clickedApartmentId);
          onTabletApartmentListClick?.(clickedApartmentId);
        } else {
          // User tapped on empty area -> Clear selection
          onApartmentSVGClear?.();
        }
      }
      
      // Reset apartment detection state
      setTouchStartedOnApartment(false);
      setTouchStartTime(0);
      setTouchStartPosition({ x: 0, y: 0 });
    }
    
    // Handle tablet mouse clicks on apartments (similar to mobile touch logic)
    if (isTablet && touchStartedOnApartment && e && 'clientX' in e) {
      const movementDistance = Math.sqrt(
        Math.pow(e.clientX - touchStartPosition.x, 2) + 
        Math.pow(e.clientY - touchStartPosition.y, 2)
      );
      
      // Handle mouse click with minimal movement (like tap on mobile)
      if (movementDistance < 15) { // Tolerance for click vs drag
        // Find the target element at click position
        let target = document.elementFromPoint(e.clientX, e.clientY);
        
        // Fallback if elementFromPoint fails
        if (!target) {
          target = e.target as HTMLElement;
        }
        
        // Check if clicking on apartment SVG path
        let clickedApartmentId: string | null = null;
        
        // First try: Check the direct target element
        if (target && target.tagName === 'path' && target.hasAttribute('data-apartment-id')) {
          clickedApartmentId = target.getAttribute('data-apartment-id');
        }
        
        // Second try: Use elementFromPoint to check what's actually under the mouse coordinates
        if (!clickedApartmentId) {
          const elementUnderMouse = document.elementFromPoint(e.clientX, e.clientY);
          if (elementUnderMouse && elementUnderMouse.tagName === 'path' && elementUnderMouse.hasAttribute('data-apartment-id')) {
            clickedApartmentId = elementUnderMouse.getAttribute('data-apartment-id');
          }
        }
        
        if (clickedApartmentId) {
          // User clicked on apartment SVG -> Select this apartment
          handleApartmentClick(clickedApartmentId);
        } else {
          // User clicked on empty area -> Clear selection
          onApartmentSVGClear?.();
        }
      }
    }
    
    if (isDragging && isMobile) {
      onRotationChange?.(false);
    } else if (isDragging && isTablet) {
      // TABLET: Enhanced touch end with momentum
      const currentTime = performance.now();
      const dragDuration = currentTime - dragStartTime.current;
      const isRealDrag = totalDragDistance.current > 50 && dragDuration > 100;
      
      if (isRealDrag) {
        setDesktopRotationCount(prev => prev + 1);
        
        // Apply momentum if user was moving fast enough
        if (Math.abs(touchVelocity.current) > 0.3) {
          touchMomentum.current = touchVelocity.current;
          animateMomentum(touchMomentum.current, currentIndex);
        } else {
          onRotationChange?.(false);
        }
      } else {
        onRotationChange?.(false);
      }
    } else if (isDragging && isDesktop) {
      // DESKTOP: Original logic
      const dragDuration = performance.now() - dragStartTime.current;
      const isRealDrag = totalDragDistance.current > 100 && dragDuration > 150;
      
      if (isRealDrag) {
        setDesktopRotationCount(prev => prev + 1);
      }
      onRotationChange?.(false);
    }
    
    console.log('onDragEnd - resetting isDragging to false, was dragging:', isDragging);
    setIsDragging(false);
    setIsPanning(false);
    setTouchStartedOnApartment(false); // Reset apartment click state
    onTouchStateChange?.(false);
  };

  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isMobile) {
      const touch = e.changedTouches[0];
      const movementDistance = Math.sqrt(
        Math.pow(touch.clientX - touchStartPosition.x, 2) + 
        Math.pow(touch.clientY - touchStartPosition.y, 2)
      );
      const wasRotation = totalDragDistance.current > 15;
      
      // Handle tap only when there was no rotation swipe
      if (!wasRotation && movementDistance < 15) {
        // Find the target element using multiple strategies for post-rotation robustness
        let target = document.elementFromPoint(touch.clientX, touch.clientY);
        
        // Fallback: if elementFromPoint fails (can happen after rotation), try alternative methods
        if (!target) {
          target = e.target as HTMLElement;
        }
        
        // Simple and robust detection: Check if clicking on apartment SVG path
        let clickedApartmentId: string | null = null;
        
        // First try: Check the direct target element
        if (target && target.tagName === 'path' && target.hasAttribute('data-apartment-id')) {
          clickedApartmentId = target.getAttribute('data-apartment-id');
        }
        
        // Second try: Use elementFromPoint to check what's actually under the touch coordinates
        if (!clickedApartmentId) {
          const elementUnderTouch = document.elementFromPoint(touch.clientX, touch.clientY);
          if (elementUnderTouch && elementUnderTouch.tagName === 'path' && elementUnderTouch.hasAttribute('data-apartment-id')) {
            clickedApartmentId = elementUnderTouch.getAttribute('data-apartment-id');
          }
        }
        
        if (clickedApartmentId) {
          // User clicked on apartment SVG -> Select this apartment
          handleApartmentClick(clickedApartmentId);
        } else {
          // User clicked on empty area -> Clear selection (show all text overlays, hide all SVG overlays)
          onApartmentSVGClear?.();
          setSelectedApartmentForTooltip(null);
          setIsTooltipPositionedBelow(false);
        }
      }
      
      // Reset touch state
      setTouchStartedOnApartment(false);
    }
    
    // Call the common end logic
    onDragEnd();
  };

  const handleArrowClick = (direction: 'left' | 'right') => {
    // Reduziert auf 2, da wir nur noch 25 Bilder haben
    const skipAmount = 2;
    let newIndex: number;
    
    if (direction === 'left') {
      newIndex = currentIndex - skipAmount;
    } else {
      newIndex = currentIndex + skipAmount;
    }
    
    const len = images.length;
    newIndex = ((newIndex % len) + len) % len;
    
    onIndexChange(newIndex);
  };



  const resetPan = () => {
    setPanX(0);
  };

  // Handle mouse move for tooltip positioning - throttled to prevent excessive re-renders
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Only update mouse position if we actually need to show a tooltip
    if (effectiveHoveredApartmentForTooltip) {
      setMousePosition({ x: e.clientX, y: e.clientY });
    }
  }, [effectiveHoveredApartmentForTooltip]);



  // Handle clicks outside apartments on mobile to clear selection
  // Note: on mobile, taps are handled in onTouchEnd. Ignoring click here prevents
  // synthetic click-after-swipe from clearing the apartment selection.
  const handleContainerClick = (e: React.MouseEvent) => {
    if (isMobile) return;
    
    // Simple and robust detection: Check if clicking on apartment SVG path
    let clickedApartmentId: string | null = null;
    const target = e.target as HTMLElement;
    
    // First try: Check the direct target element
    if (target && target.tagName === 'path' && target.hasAttribute('data-apartment-id')) {
      clickedApartmentId = target.getAttribute('data-apartment-id');
    }
    
    // Second try: Use elementFromPoint to check what's actually under the click coordinates
    if (!clickedApartmentId) {
      const elementUnderClick = document.elementFromPoint(e.clientX, e.clientY);
      if (elementUnderClick && elementUnderClick.tagName === 'path' && elementUnderClick.hasAttribute('data-apartment-id')) {
        clickedApartmentId = elementUnderClick.getAttribute('data-apartment-id');
      }
    }
    
    if (clickedApartmentId) {
      // User clicked on apartment SVG -> Select this apartment
      handleApartmentClick(clickedApartmentId);
    } else {
      // User clicked on empty area -> Clear selection (show all text overlays, hide all SVG overlays)
      onApartmentSVGClear?.();
      setSelectedApartmentForTooltip(null);
      setIsTooltipPositionedBelow(false);
    }
  };

  // Handle apartment hover events
  const handleApartmentHover = (apartmentId: string | null) => {
    setHoveredApartment(apartmentId);
    onApartmentHover?.(apartmentId);
  };

  // Use the callback from parent for apartment clicks
  const handleApartmentClick = (apartmentId: string) => {
    const isMobile = window.innerWidth <= 850;
    const isTablet = window.innerWidth > 850 && window.innerWidth <= 1300;
    
    
    if (isMobile) {
      // On mobile: replace selection with this apartment (do not clear first —
      // clear+select can race and leave selection empty)
      onApartmentSVGSelect?.(apartmentId);
      setSelectedApartmentForTooltip(apartmentId);
      updateTooltipPosition(apartmentId, currentIndex);
    } else if (isTablet) {
      // On tablet: behave like desktop - directly open side panel (no tooltip)
      onApartmentClick?.(apartmentId);
    } else {
      // On desktop, use parent callback (opens side panel)
      onApartmentClick?.(apartmentId);
    }
  };

  // Mobile apartment detail opening - handle Details button clicks from tooltip
  const handleMobileApartmentDetailsClick = (apartmentId: string) => {
    // Use the dedicated onMobileApartmentDetails prop instead of onApartmentClick
    if (onMobileApartmentDetails) {
      onMobileApartmentDetails(apartmentId);
    }
  };

  // Function to get apartment center position from SVG paths
  const getApartmentCenterPosition = async (apartmentId: string, frameIndex: number): Promise<{ x: number, y: number } | null> => {
    try {
      // Load SVG data if not available (use static variable to cache)
      let svgData = (window as any)._cachedSVGData;
      if (!svgData) {
        const response = await fetch('/SVG_Paths.json');
        svgData = await response.json();
        (window as any)._cachedSVGData = svgData;
      }
      
      const frameKey = (frameIndex + 1).toString().padStart(4, '0');
      const currentFrame = svgData.frames[frameKey];
      
      if (!currentFrame || !currentFrame.apartments || !currentFrame.apartments[apartmentId]) {
        return null;
      }
      
      const apartment = currentFrame.apartments[apartmentId];
      if (!apartment.paths || apartment.paths.length === 0) {
        return null;
      }
      
      // Get the first path and calculate its bounding box center
      const pathData = apartment.paths[0].path_data;
      
      // Simple path parser to get approximate center
      const pathCommands = pathData.match(/[ML][\d\s,.-]+/g);
      if (!pathCommands) return null;
      
      const points: Array<{x: number, y: number}> = [];
      pathCommands.forEach((command: string) => {
        const coords = command.substring(1).split(/[\s,]+/).filter(Boolean);
        for (let i = 0; i < coords.length; i += 2) {
          if (coords[i + 1]) {
            points.push({
              x: parseFloat(coords[i]),
              y: parseFloat(coords[i + 1])
            });
          }
        }
      });
      
      if (points.length === 0) return null;
      
      // Calculate center point
      const avgX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
      const avgY = points.reduce((sum, p) => sum + p.y, 0) / points.length;
      
      // Convert SVG coordinates (1920x1080) to screen coordinates
      const container = containerRef.current;
      if (!container) return null;
      
      const containerRect = container.getBoundingClientRect();
      const scaleX = containerRect.width / 1920;
      const scaleY = containerRect.height / 1080;
      
      // Account for mobile panning
      const panOffset = isMobile ? panX : 0;
      
      const screenX = (avgX * scaleX) + containerRect.left + panOffset;
      const screenY = (avgY * scaleY) + containerRect.top;
      
      return { x: screenX, y: screenY };
    } catch (error) {
      console.error('Error calculating apartment position:', error);
      return null;
    }
  };

  // Update tooltip position based on apartment position in current frame
  const updateTooltipPosition = async (apartmentId: string, frameIndex: number) => {
    if (!isMobile) return;
    
    const position = await getApartmentCenterPosition(apartmentId, frameIndex);
    if (position) {
      // Check if tooltip would go outside the viewer bounds
      const tooltipHeight = 120; // Approximate tooltip height (with Details button)
      const tooltipOffset = 200; // Current offset above apartment
      const viewerContainer = containerRef.current;
      
      if (viewerContainer) {
        const viewerRect = viewerContainer.getBoundingClientRect();
        const tooltipTopY = position.y - tooltipOffset;
        
        // If tooltip would go above viewer area, position it below the apartment instead
        if (tooltipTopY < viewerRect.top) {
          setMousePosition({
            x: position.x,
            y: position.y + 20 // Position below apartment with smaller offset
          });
          setIsTooltipPositionedBelow(true);
        } else {
          setMousePosition({
            x: position.x,
            y: position.y
          });
          setIsTooltipPositionedBelow(false);
        }
      } else {
        setMousePosition({
          x: position.x,
          y: position.y
        });
        setIsTooltipPositionedBelow(false);
      }
    }
  };

  // Update tooltip position when image changes (for selected apartment on mobile)
  useEffect(() => {
    if (isMobile && selectedApartmentForTooltip && selectedApartments && selectedApartments.length > 0) {
      updateTooltipPosition(selectedApartmentForTooltip, currentIndex);
    }
  }, [currentIndex, selectedApartmentForTooltip, isMobile, panX]);

  // Update maxPan when container size changes
  useEffect(() => {
    const updateMaxPan = () => {
      if (containerRef.current && isMobile) {
        const containerWidth = containerRef.current.offsetWidth;
        const imageWidth = containerWidth * 3; // 300% of container width
        const calculatedMaxPan = (imageWidth - containerWidth) / 2;
        setMaxPan(calculatedMaxPan);
      }
    };

    updateMaxPan();
    window.addEventListener('resize', updateMaxPan);
    return () => window.removeEventListener('resize', updateMaxPan);
  }, [isMobile]);

  const getInstructionText = () => {
    if (isMobile) {
      if (isCurrentlyRotating) {
        return "";
      }
      return isDragging ? "360° drehen..." : "Wischen zum Drehen der 360° Ansicht";
    }
    
    // Desktop instructions - more detailed and helpful with progress feedback
    if (isDragging) {
      if (desktopRotationCount === 0) {
        return "Perfekt! So drehen Sie die Ansicht.";
      } else {
        return "Excellent! Weiter so!";
      }
    }
    
    if (desktopRotationCount === 0) {
      return "Mit Maus ziehen für interaktive 360° Rundumansicht";
    } else if (desktopRotationCount === 5) {
      return "Sehr gut! Probieren Sie verschiedene Ansichten aus";
    }
    
    return ""; // No instructions after 2 successful rotations
  };

  const getInstructionIcon = () => {
    if (isMobile) {
      if (isCurrentlyRotating) {
        return null;
      }
      return isDragging ? <Target size={24} /> : <Pointer size={24} />;
    }
    return <Mouse size={24} />;
  };

  // Enhanced instruction visibility logic for desktop, tablet, and mobile
  const shouldShowInstructions = !isMobile && !isTablet 
    ? (showInstructions && desktopRotationCount < 2) || (isDragging && desktopRotationCount < 2)
    : isMobile 
      ? (!hasUserInteracted || isDragging)
      : false;
  const shouldShowCenterButton = false;

  const ViewerContent = (
    <ViewerContainer onClick={handleContainerClick}>
      <ImageContainer 
        ref={containerRef}
        isDragging={isDragging}
        isPanning={isPanning}
        onMouseDown={onDragStart}
        onMouseMove={(e) => {
          onDragMove(e);
          handleMouseMove(e);
        }}
        onMouseUp={(e) => onDragEnd(e)}
        onMouseLeave={(e) => onDragEnd(e)}
        onTouchStart={onDragStart}
        onTouchMove={onDragMove}
        onTouchEnd={onTouchEnd}
      >
                 <ImageStack>
           {images.map((src, index) => (
             <StackedPanoramaImage
               key={index}
               src={src}
               alt={`360° View ${index + 1}`}
               panX={isMobile ? panX : 0}
               isVisible={index === currentIndex}
               imageIndex={index}
               loading="eager"
               decoding="sync"
             />
           ))}
         </ImageStack>

        {/* SVG Text Overlay - always show on mobile, controlled by textSVGVisible toggle on desktop - render first so interactive areas are on top */}
        {showTextOverlay && (isMobile || textSVGVisible) && (
          isRealView ? (
            <RealViewSVGTextOverlayComponent
              currentIndex={currentIndex}
              panX={isMobile ? panX : 0}
              isMobile={isMobile}
              hoveredApartmentId={effectiveHoveredApartmentForSVG}
              selectedApartments={selectedApartments}
              apartmentColors={apartmentColors}
              apartments={apartments}
              isMultiSelectMode={isMobile ? false : isMultiSelectMode}
            />
          ) : (
            <SVGTextOverlayComponent
              currentIndex={currentIndex}
              panX={isMobile ? panX : 0}
              isMobile={isMobile}
              hoveredApartmentId={effectiveHoveredApartmentForSVG}
              selectedApartments={selectedApartments}
              apartmentColors={apartmentColors}
              apartments={apartments}
              isMultiSelectMode={isMobile ? false : isMultiSelectMode}
            />
          )
        )}

        {/* SVG Overlay for apartment highlighting - render second so it's interactive */}
        {showSVGOverlay && svgOverlayVisible && (
          isRealView ? (
            <RealViewSVGOverlayComponent
              currentIndex={currentIndex}
              panX={isMobile ? panX : 0}
              isMobile={isMobile}
              onApartmentClick={handleApartmentClick}
              onApartmentHover={handleApartmentHover}
              selectedApartments={selectedApartments}
              apartmentColors={apartmentColors}
              hoveredApartment={effectiveHoveredApartmentForSVG}
              selectedApartmentsSVG={selectedApartments}
              apartments={apartments}
              isDragging={isDragging}
              isMultiSelectMode={isMobile ? false : isMultiSelectMode}
            />
          ) : (
            <SVGOverlayComponent
              currentIndex={currentIndex}
              panX={isMobile ? panX : 0}
              isMobile={isMobile}
              onApartmentClick={handleApartmentClick}
              onApartmentHover={handleApartmentHover}
              selectedApartments={selectedApartments}
              apartmentColors={apartmentColors}
              hoveredApartment={effectiveHoveredApartmentForSVG}
              selectedApartmentsSVG={selectedApartments}
              apartments={apartments}
              isDragging={isDragging}
              isMultiSelectMode={isMobile ? false : isMultiSelectMode}
            />
          )
        )}

        <InstructionOverlay isVisible={shouldShowInstructions} isMobile={isMobile}>
          <HandIcon>
            <InstructionIcon>{getInstructionIcon()}</InstructionIcon>
          </HandIcon>
          {getInstructionText()}
        </InstructionOverlay>

        <CenterButtonOverlay isVisible={shouldShowCenterButton} onClick={resetPan}>
          <CenterIcon><RotateCcw size={16} /></CenterIcon>
          Ansicht zentrieren
        </CenterButtonOverlay>

        {isMobile && showMobileBackButton && onMobileBackToDetails && (
          <MobileBackButton type="button" onClick={onMobileBackToDetails} aria-label="Zurück zu Wohnungsdetails">
            <ArrowLeft size={16} />
            Zurück
          </MobileBackButton>
        )}

        
{isPreloading && (
          <LoadingOverlay>
            <LoadingContent>
              <LogoContainer>
                <img 
                  src="/Logo_vonZalan.svg" 
                  alt="Logo von Zalan" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1))' 
                  }} 
                />
              </LogoContainer>
              
              <div style={{
                width: isMobile ? '250px' : '400px',
                maxWidth: '80%',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                alignItems: 'center'
              }}>
                <div style={{
                  width: '100%',
                  height: isMobile ? '6px' : '10px',
                  background: 'rgba(11, 39, 73, 0.1)',
                  borderRadius: '5px',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 2px 6px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{
                    width: `${preloadProgress}%`,
                    height: '100%',
                    background: 'linear-gradient(135deg, #0b2749, #1a4d8f)',
                    borderRadius: '5px',
                    transition: 'width 0.3s ease',
                    boxShadow: '0 0 15px rgba(11, 39, 73, 0.4)'
                  }} />
                </div>
                <p style={{
                  color: '#1f2937',
                  fontSize: isMobile ? '32px' : '48px',
                  fontWeight: '700',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  margin: 0
                }}>
                  {Math.round(preloadProgress)}%
                </p>
              </div>
            </LoadingContent>
          </LoadingOverlay>
        )}
      </ImageContainer>

      {/* Desktop Arrow Navigation Buttons */}
      <ArrowButton 
        direction="left" 
        onClick={() => handleArrowClick('left')}
        disabled={isPreloading}
        aria-label="20 Bilder zurück"
      >
        ←
      </ArrowButton>
      
      <ArrowButton 
        direction="right" 
        onClick={() => handleArrowClick('right')}
        disabled={isPreloading}
        aria-label="20 Bilder vorwärts"
      >
        →
      </ArrowButton>

              {/* Apartment Tooltip - desktop only; mobile uses bottom info panel */}
              {!isMobile && (
                <ApartmentTooltip 
                  apartmentId={effectiveHoveredApartmentForTooltip} 
                  mousePosition={mousePosition}
                  onDetailsClick={handleMobileApartmentDetailsClick}
                  onApartmentListClick={onTabletApartmentListClick}
                  isPositionedBelow={isTooltipPositionedBelow}
                />
              )}

        {/* Mobile Apartment Popup - replaced by Details button in tooltip */}
    </ViewerContainer>
  );

  const selectedMobileApartmentId = selectedApartments && selectedApartments.length > 0
    ? selectedApartments[0]
    : null;
  const selectedMobileDataId = selectedMobileApartmentId
    ? mapSVGIdToDataId(selectedMobileApartmentId)
    : null;
  const selectedMobileApartment = selectedMobileDataId
    ? (apartments.find(apt => apt.id === selectedMobileDataId)
      || defaultApartments.find(apt => apt.id === selectedMobileDataId)
      || null)
    : null;

  const getMobileApartmentStatus = (apt: typeof selectedMobileApartment) => {
    if (!apt) return 'available';
    if ((apt as any).status) return (apt as any).status;
    return apt.available !== false ? 'available' : 'reserved';
  };

  const MobileSwipeStripContent = (
    <MobileSwipeStrip
      onTouchStart={(e) => {
        const touch = e.touches[0];
        (e.currentTarget as any)._swipeStartX = touch.clientX;
        (e.currentTarget as any)._swipeStartIndex = currentIndex;
        totalDragDistance.current = 0;
        onTouchStateChange?.(true);
        onRotationChange?.(true);
      }}
      onTouchMove={(e) => {
        e.preventDefault();
        const startX = (e.currentTarget as any)._swipeStartX;
        const startIndex = (e.currentTarget as any)._swipeStartIndex;
        if (typeof startX !== 'number') return;
        const deltaX = e.touches[0].clientX - startX;
        totalDragDistance.current = Math.abs(deltaX);
        const frames = Math.round(deltaX / 12);
        const total = images.length || 1;
        const nextIndex = ((startIndex - frames) % total + total) % total;
        onIndexChange(nextIndex);
      }}
      onTouchEnd={() => {
        onTouchStateChange?.(false);
        onRotationChange?.(false);
      }}
      onTouchCancel={() => {
        onTouchStateChange?.(false);
        onRotationChange?.(false);
      }}
    >
      <MobileSwipeArrow>
        <ChevronsLeft size={22} strokeWidth={2.25} />
      </MobileSwipeArrow>
      <MobileSwipeArrow>
        <ChevronsRight size={22} strokeWidth={2.25} />
      </MobileSwipeArrow>
    </MobileSwipeStrip>
  );

  const MobileControlsContent = selectedMobileApartment && selectedMobileApartmentId ? (
    <MobileControls>
      <MobileApartmentInfoPanel>
        <MobileApartmentHeader>
          <MobileApartmentTitle>{selectedMobileApartment.name}</MobileApartmentTitle>
          <MobileApartmentClearButton
            type="button"
            aria-label="Auswahl aufheben"
            onClick={() => {
              setSelectedApartmentForTooltip(null);
              onApartmentSVGClear?.();
            }}
          >
            ×
          </MobileApartmentClearButton>
        </MobileApartmentHeader>

        <MobileApartmentMeta>
          <MobileApartmentMetaItem>
            <MobileApartmentMetaLabel>Zimmer</MobileApartmentMetaLabel>
            <MobileApartmentMetaValue>{selectedMobileApartment.rooms}</MobileApartmentMetaValue>
          </MobileApartmentMetaItem>
          <MobileApartmentMetaItem>
            <MobileApartmentMetaLabel>Größe</MobileApartmentMetaLabel>
            <MobileApartmentMetaValue>{selectedMobileApartment.size} m²</MobileApartmentMetaValue>
          </MobileApartmentMetaItem>
          <MobileApartmentMetaItem>
            <MobileApartmentMetaLabel>Preis</MobileApartmentMetaLabel>
            <MobileApartmentMetaValue $reserved={getMobileApartmentStatus(selectedMobileApartment) !== 'available'}>
              {(() => {
                const status = getMobileApartmentStatus(selectedMobileApartment);
                if (status === 'reserved') return 'Reserviert';
                if (status === 'sold') return 'Verkauft';
                return formatPrice(selectedMobileApartment.price);
              })()}
            </MobileApartmentMetaValue>
          </MobileApartmentMetaItem>
        </MobileApartmentMeta>

        <MobileApartmentDetailsButton
          type="button"
          onClick={() => handleMobileApartmentDetailsClick(selectedMobileApartmentId)}
        >
          Details
        </MobileApartmentDetailsButton>
      </MobileApartmentInfoPanel>
      {mobileOverlayMenu}
    </MobileControls>
  ) : (
    <MobileControls>
      <div style={{
        textAlign: 'center',
        color: '#94a3b8',
        fontSize: '13px',
        fontWeight: 500,
        padding: '8px 0'
      }}>
        Tippe auf eine Wohnung im Gebäude
      </div>
      {mobileOverlayMenu}
    </MobileControls>
  );

  // Mobile: viewer, thin swipe strip between image and panel, then details
  if (isMobile) {
    return (
      <MobileWrapper>
        {ViewerContent}
        {MobileSwipeStripContent}
        {MobileControlsContent}
      </MobileWrapper>
    );
  }

  // Tablet: Fullscreen with horizontal scroll bar at bottom
  if (isTablet) {
    return (
      <MobileWrapper>
        {ViewerContent}
        {/* Tablet gets horizontal scroll bar instead of circular control */}
        <TabletScrollBar
          totalImages={images.length}
          currentIndex={currentIndex}
          onIndexChange={handleGreenWheelRotation}
        />
        {/* Keep mobile overlay menu if needed */}
        {mobileOverlayMenu && (
          <MobileControls>
            {mobileOverlayMenu}
          </MobileControls>
        )}
      </MobileWrapper>
    );
  }

  // Desktop: Fullscreen without bottom controls
  return ViewerContent;
};

// Tooltip Component
const TooltipContainer = styled.div<{ x: number; y: number; visible: boolean; isMobile?: boolean; isBelow?: boolean; isInteractive?: boolean }>`
  position: fixed;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: #1f2937;
  padding: 14px 18px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  z-index: 9999;
  pointer-events: ${props => (props.isInteractive || props.isMobile) ? 'auto' : 'none'};
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease, visibility 0.2s ease; /* No position transitions for desktop */
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1);
  max-width: 280px;
  white-space: nowrap;
  border: 1px solid rgba(0, 0, 0, 0.08);

  /* Center tooltip above cursor with small arrow effect */
  transform: translateX(-50%);
  
  /* Add arrow pointing down or up based on position */
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    border: 7px solid transparent;
    pointer-events: none;
    
    /* Default: arrow pointing down (tooltip above apartment) */
    top: 100%;
    border-top-color: rgba(255, 255, 255, 0.95);
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  }

  /* When tooltip is below apartment, arrow points up */
  ${props => props.isBelow && css`
    &::after {
      top: auto;
      bottom: 100%;
      border-top-color: transparent;
      border-bottom-color: rgba(255, 255, 255, 0.95);
      filter: drop-shadow(0 -2px 4px rgba(0, 0, 0, 0.1));
    }
  `}

  @media (max-width: 850px) {
    font-size: 13px;
    padding: 12px 16px 16px 16px;
    max-width: 260px;
    white-space: normal;
    pointer-events: auto;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); /* Smooth transitions on mobile */
    
    &::after {
      border-width: 5px;
    }
  }
`;

const TooltipTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  color: #111827;
  font-size: 16px;
`;

const TooltipDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TooltipDetail = styled.div`
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  
  @media (max-width: 850px) {
    font-size: 12px;
  }
`;

const TooltipDetailReserved = styled.div`
  color: #dc2626;
  font-size: 14px;
  font-weight: 600;
  background: rgba(239, 68, 68, 0.1);
  padding: 2px 8px;
  border-radius: 6px;
  display: inline-block;
  
  @media (max-width: 850px) {
    font-size: 12px;
  }
`;

const TooltipDetailsButton = styled.button`
  display: none;
  
  @media (max-width: 850px) {
    display: block;
    background: #3b82f6;
    color: white;
    border: 1px solid #2563eb;
    border-radius: 8px;
    padding: 10px 16px;
    font-size: 13px;
    font-weight: 600;
    margin-top: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
    backdrop-filter: blur(10px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
    
    &:hover, &:active {
      background: #2563eb;
      border-color: #1d4ed8;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
    }
    
    &:active {
      transform: translateY(0px);
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
    }
  }
`;

// Button for Tablet devices to open apartment list (like desktop click behavior)
const TooltipApartmentListButton = styled.button`
  /* Always visible by default - no display:none */
  background: #059669;
  color: white;
  border: 1px solid #047857;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  margin-top: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
  width: 100%;
  
  &:hover, &:active {
    background: #047857;
    border-color: #065f46;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.4);
  }
  
  &:active {
    transform: translateY(0px);
    box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
  }
`;


// Apartment Info Popup Component
const ApartmentPopupContainer = styled.div<{ isDragging: boolean }>`
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  /* Glassmorphic background - same as apartment list */
  background: linear-gradient(135deg, 
    rgba(248, 250, 252, 0.9), 
    rgba(241, 245, 249, 0.8),
    rgba(226, 232, 240, 0.7)
  );
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  border: 1px solid rgba(226, 232, 240, 0.6);
  min-width: 280px;
  max-width: 320px;
  animation: popupSlideUp 0.3s ease-out;
  pointer-events: ${props => props.isDragging ? 'none' : 'auto'};
  transition: opacity 0.2s ease;
  opacity: ${props => props.isDragging ? 0.7 : 1};
  
  /* Add subtle pattern overlay - same as apartment list */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.2) 1px, transparent 1px),
      radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
    background-size: 20px 20px;
    background-position: 0 0, 10px 10px;
    pointer-events: none;
    z-index: 0;
    border-radius: 12px;
  }

  @keyframes popupSlideUp {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  @media (max-width: 850px) {
    bottom: 20px;
    min-width: 260px;
    max-width: 300px;
    margin: 0 20px;
    left: 50%;
  }
`;

const PopupHeader = styled.div`
  padding: 16px 20px 12px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.4);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
  background: transparent;
`;

const PopupTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  color: white;
  
  @media (max-width: 850px) {
    color: #333;
  }
`;

const CloseButton = styled.button`
  background: #f1f3f4;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  color: #5f6368;
  transition: all 0.2s ease;
  z-index: 1000;
  line-height: 1;
  padding: 0;
  text-align: center;

  &:hover {
    background: #e8eaed;
    transform: rotate(90deg);
  }

  @media (max-width: 850px) {
    width: 48px;
    height: 48px;
    font-size: 20px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4);
    color: #374151;
    font-weight: 600;
    min-height: 48px;
    min-width: 48px;
    
    &:hover {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.95));
      transform: scale(1.05);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    }
    
    &:active {
      transform: scale(0.95);
      transition: transform 0.1s ease;
    }
  }
`;

const PopupContent = styled.div`
  padding: 16px 20px;
  position: relative;
  z-index: 1;
  background: transparent;
`;

const PopupDetail = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
  
  &:last-child {
    margin-bottom: 16px;
  }
`;

const PopupLabel = styled.span`
  color: #666;
`;

const PopupValue = styled.span`
  font-weight: 500;
  color: white;
`;

const InfoButton = styled.button`
  width: 100%;
  background: #2563eb;
  color: white;
  border: none;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-bottom: 8px;
  
  &:hover {
    background: #1d4ed8;
  }
`;

const DownloadButton = styled.button`
  width: 100%;
  background: #16a34a;
  color: white;
  border: none;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #15803d;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const ApartmentPopup: React.FC<{
  apartmentId: string;
  onClose: () => void;
  onOpenInfo: () => void;
  isDragging: boolean;
}> = ({ apartmentId, onClose, onOpenInfo, isDragging }) => {
  const { apartments: contextApartments } = useApartments();
  const apartments = contextApartments.length > 0 ? contextApartments : defaultApartments;
  const [isDownloading, setIsDownloading] = useState(false);
  
  const dataId = mapSVGIdToDataId(apartmentId);
  const apartment = dataId ? apartments.find(apt => apt.id === dataId) : null;
  
  if (!apartment) return null;

  // Download floor plan PDF function
  const downloadFloorPlan = async () => {
    try {
      setIsDownloading(true);
      
      // Generate PDF URL using the new naming convention (e.g., Wohnung A1.1.pdf)
      const cloudfrontUrl = 'https://d2tbav89ixiad0.cloudfront.net';
      const pdfName = getApartmentPDFName(apartment.id);
      const pdfUrl = `${cloudfrontUrl}/PDF-Grundrisse/${pdfName}.pdf`;
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${pdfName}.pdf`;
      link.target = '_blank';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error downloading floor plan:', error);
      alert('Fehler beim Herunterladen des Grundrisses. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Zentrale Preisformatierung wird jetzt verwendet

  const formatSize = (size: number) => {
    return `${size} m²`;
  };

  return (
    <ApartmentPopupContainer isDragging={isDragging}>
      <PopupHeader>
        <PopupTitle>Wohnung {apartmentId}</PopupTitle>
        <CloseButton onClick={onClose}>×</CloseButton>
      </PopupHeader>
      <PopupContent>
        <PopupDetail>
          <PopupLabel>Zimmer:</PopupLabel>
          <PopupValue>{apartment.rooms}</PopupValue>
        </PopupDetail>
        <PopupDetail>
          <PopupLabel>Größe:</PopupLabel>
          <PopupValue>{formatSize(apartment.size)}</PopupValue>
        </PopupDetail>
        <PopupDetail>
          <PopupLabel>Preis:</PopupLabel>
          {apartment.available ? (
            <PopupValue>{formatPrice(apartment.price)}</PopupValue>
          ) : (
            <PopupValue style={{ color: '#ef4444', fontWeight: 600 }}>Reserviert</PopupValue>
          )}
        </PopupDetail>
        <InfoButton onClick={onOpenInfo}>
          Weitere Informationen
        </InfoButton>
        <DownloadButton 
          onClick={downloadFloorPlan}
          disabled={isDownloading}
        >
          {isDownloading ? 'Lädt herunter...' : 'Grundriss Herunterladen'}
        </DownloadButton>
      </PopupContent>
    </ApartmentPopupContainer>
  );
};

// Simple test tooltip component
const SimpleTooltip: React.FC<{
  apartmentId: string | null;
  mousePosition: { x: number; y: number };
}> = ({ apartmentId, mousePosition }) => {
  if (!apartmentId) return null;
  
  return (
    <div style={{
      position: 'fixed',
      left: mousePosition.x + 10,
      top: mousePosition.y - 100,
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      color: '#1f2937',
      padding: '12px 16px',
      borderRadius: '12px',
      fontSize: '15px',
      zIndex: 9999,
      pointerEvents: 'none',
      border: '1px solid rgba(0, 0, 0, 0.08)',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
      maxWidth: '200px'
    }}>
      <div style={{ fontWeight: '600', marginBottom: '6px', color: '#111827' }}>
        Wohnung {apartmentId}
      </div>
      <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>
        Tooltip is working!
      </div>
    </div>
  );
};

// Mapping function to convert SVG apartment IDs (A1, A2, B1, etc.) to data IDs (1, 2, 12, etc.)
const mapSVGIdToDataId = (svgId: string): number | null => {
  // SVG uses format like: A1, A2, A3, ..., A11, B1, B2, B3, ..., B11
  // Data uses numeric IDs: 1-22
  
  const match = svgId.match(/^([AB])(\d+)$/);
  if (!match) return null;
  
  const [, building, apartmentNum] = match;
  const num = parseInt(apartmentNum, 10);
  
  if (building === 'A') {
    // A1-A11 maps to IDs 1-11
    return num;
  } else if (building === 'B') {
    // B1-B11 maps to IDs 12-22
    return num + 11;
  }
  
  return null;
};

// Apartment Tooltip Component
const ApartmentTooltip: React.FC<{
  apartmentId: string | null;
  mousePosition: { x: number; y: number };
  onDetailsClick?: (apartmentId: string) => void;
  onApartmentListClick?: (apartmentId: string) => void;
  isPositionedBelow?: boolean;
}> = ({ apartmentId, mousePosition, onDetailsClick, onApartmentListClick, isPositionedBelow = false }) => {
  const { apartments: contextApartments } = useApartments();
  
  // Use context apartments if available, otherwise fall back to default data
  const apartments = contextApartments.length > 0 ? contextApartments : defaultApartments;
  
  if (!apartmentId) {
    return null;
  }
  
  // Convert SVG apartment ID to data ID
  const dataId = mapSVGIdToDataId(apartmentId);
  
  if (!dataId) {
    return <SimpleTooltip apartmentId={apartmentId} mousePosition={mousePosition} />;
  }
  
  const apartment = apartments.find(apt => apt.id === dataId);
  if (!apartment) {
    return <SimpleTooltip apartmentId={apartmentId} mousePosition={mousePosition} />;
  }

  // Zentrale Preisformatierung wird jetzt verwendet

  const formatSize = (size: number) => {
    return `${size} m²`;
  };

  // Detect mobile and tablet
  const isMobile = window.innerWidth <= 850;
  const isTablet = window.innerWidth > 850 && window.innerWidth <= 1300;
  

  const handleDetailsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDetailsClick && apartmentId) {
      onDetailsClick(apartmentId);
    }
  };

  const handleApartmentListClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onApartmentListClick && apartmentId) {
      onApartmentListClick(apartmentId);
    }
  };

  return (
    <TooltipContainer 
      x={mousePosition.x} 
      y={mousePosition.y - (isMobile && !isPositionedBelow ? 200 : (isMobile && isPositionedBelow ? -50 : 180))} 
      visible={true}
      isMobile={isMobile}
      isBelow={isMobile && isPositionedBelow}
      isInteractive={isMobile}
    >
      <TooltipTitle>{apartment.name}</TooltipTitle>
      <TooltipDetails>
        <TooltipDetail>{apartment.rooms} Zimmer</TooltipDetail>
        <TooltipDetail>{formatSize(apartment.size)}</TooltipDetail>
        {(() => {
          // Get apartment status with backward compatibility
          const getApartmentStatus = (apt: any) => {
            if (apt.status) {
              return apt.status;
            }
            // Backward compatibility: convert available boolean to status
            return apt.available !== false ? 'available' : 'sold';
          };

          const status = getApartmentStatus(apartment);
          
          switch (status) {
            case 'available':
              return <TooltipDetail>{formatPrice(apartment.price)}</TooltipDetail>;
            case 'reserved':
              return <TooltipDetailReserved>Reserviert</TooltipDetailReserved>;
            case 'sold':
              return <TooltipDetailReserved>Verkauft</TooltipDetailReserved>;
            default:
              return <TooltipDetail>{formatPrice(apartment.price)}</TooltipDetail>;
          }
        })()}
      </TooltipDetails>
      {isMobile && onDetailsClick && (
        <TooltipDetailsButton onClick={handleDetailsClick}>
          Details
        </TooltipDetailsButton>
      )}
      {/* Tablet Button - DISABLED: Tablet now behaves like desktop (no tooltip) */}
    </TooltipContainer>
  );
};

export default Viewer360; 